import { HUNKER_ABILITY, HUNKER_STATUS, hunkerUnavailableReason, bindHunkerStatus, type HunkerObject } from './hunker.js';
import type { ForcedMovement } from '../movement/forced-movement-result';
import { visibilityUnavailableMessage } from '../ability/visibility.js';
import { withSurfaceReactionFrame, surfaceDamageImpact } from '../ability/surface-reactions.js';
// janus-engine/actions/ability-use.ts — the GENERIC ability-use pipeline's entry (2026-09-26, overnight lane): one browser resolver
// for every player-started ability / spell, replacing the per-ability slices (A1 Dodge, A2 one attack). It returns exactly what PHP's
// janus() would have written — the roll-log rows in order, encounter_creatures changes, status rows, inventory changes —
// or PHP's refusal message, or an explicit hand-back naming the branch it does not model yet.
//
// PROOF BAR: the offline replay oracle (owner 2026-09-25). tools/probes/ability_use_oracle_capture.php re-enacts every real use of the
// last 90 days through the real janus() with the draws taped; tools/test/janus-ability-oracle-census.mjs feeds this resolver the same
// before-state and the same draws (replayRollSource) and reports exact replay and wrong-answer rates.
// The pipeline itself lives in ../ability/ (a faithful transliteration of janus(); contract in ../ability/context.ts).

import { FALLING_DAMAGE_ID, resolveFallDamage } from '../ability/movement/fall-damage.js';
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import { statusCapabilityFailure } from '../ability/statuses/status-capability';
import { mergeSpawnPlans } from '../spawn/row-plan';
import { type RollSource } from './roll-source.js';
import type { TurnDeathTransition } from '../turns/end-turn.js';
import { abilityGateVerdict, type AbilityPreconditionReaders } from './ability-preconditions.js';
import { casterHoldsPassive, layerListEntryUuid } from './spell-components.js';
import { excludeHurlObjectTargets } from './object-handling-layers.js';
import { isThrowObjectCast, shapeThrowObjectCast, planThrowObjectLanding } from '../ability/throw-object.js';
import { statusProvenanceGap } from './status-provenance.js';
import { assignFreshStatusIds } from './status-row-identity.js';
import { type JanusCtx, type JanusReaders, type LogRow, type StatusRow, type InventoryChange, type DeathTailEntry, type ConcentrationBreak, type StatusStackChange, type CounterspellClaims, HandBack, Refusal, commitFaceAccepted, ecChanges, ecLean, upper, itemField, record, requiredInt, definitionNotHeld, actorId, inventoryId, isInventoryRow } from '../ability/context.js';
import { janus } from '../ability/janus.js';
import { drainAbilityDestructionPayloads } from '../ability/destruction-payload.js';
import { SKILL_ITEM_TYPES } from '../ability/templates/template-skill-check.js';
import { resolvedVerdict } from '../ability/resolved-verdict.js';
import { DEAD_DAMAGE_WARNING, DEAD_HEAL_WARNING } from '../ability/is-target-valid-for-effect.js';
import { fireContingencyCast, fireDamageContingencies, type ContingencyCast } from '../sanguine/contingency-fire.js';
export { resolvedVerdict } from '../ability/resolved-verdict.js';
import { creatureControlRoll } from '../ability/rolls/registry-dispatch-rolls.js';
import { creatureControl, DISMISS_SUMMON, RECALL_SUMMON } from '../ability/creature-control-ids.js';
import { classAuraCastPrecheck } from '../ability/class-aura-cast.js';
import { isClassAuraDefinition } from '../statuses/class-aura.js';
import { finalizePlacedAoeTargets, type PlacedAoePlacement, type PlacedAoeReaders } from '../ability/aoe/expand-placed-aoe-targets.js';
import { paintedWallForPlacement } from '../ability/aoe/painted-wall-cast.js';
import { prepareIceWallDisplacements } from '../ability/aoe/painted-wall-ice-displacements';
import type { IceWallDisplacementReaders } from '../ability/aoe/painted-wall-ice-displacement-state';
import { upcastAreaGrowthFt } from '../ability/upcast.js';
import { nextSpellPlan, prepareNextSpell, prepareNextSpellProjectile } from '../sanguine/next-spell.js';
import { dispatchNextSpellPayloads } from '../ability/next-spell.js';
import { instantProjectileCount } from '../../shared/projectile-targeting.js';
import { isVariableAmount } from '../ability/variable-amount.js';
import { variableAttackComposite } from '../ability/variable-attack.js';
import { hasMoveTowardsTarget } from '../ability/movement/move-caster-towards-target.js';
import { applyAoeSparesAllies } from '../ability/caster-spell-layers.js';
import { strayCandidates } from '../ability/stray-shot.js';
import { abilityInventoryTargetIds, inventoryDamageTarget, InventoryDamageFactsNeeded, type InventoryDamageNeed } from '../inventory/ability-damage.js';
import { wallRunActionKey } from '../inventory/wall-run-damage.js';
import { cachedInventoryRow } from '../inventory/cached-inventory-trees';
import { assertMonsterBitRemovals } from '../inventory/monster-bit-removal.js';
import { isSelfFollowAura, expandSelfFollowAuraTargets } from '../ability/aoe/expand-self-follow-aura.js';
import { statusGainTriggerPossible } from '../ability/statuses/status-trigger-gate.js';
import { PASSIVE_DYNAMIC_LAYERS, runTriggerFamily, triggerExecutorGap, triggerFamilyPreDraw, TriggerUnavailable } from '../ability/trigger-executor.js';
import { TAKES_ANY_DAMAGE, TAKES_DAMAGE_TYPE, TARGET_TAKES_ANY_DAMAGE, TARGET_TAKES_DAMAGE_TYPE } from '../ability/trigger-executor.js';
import { damageTakenTriggers, onKillGate, onKillTriggers, planDamageStatusEffects, performDamageStatusEffects, finishDamageConcentration, damageMaimedStacks, applyDamageMaimed, resolvePlayerDying } from '../ability/rolls/damage-type-roll.js';
import { patchVitalityCluster } from '../compile/held-vitality';
import { performWake } from '../ability/statuses/wake-on-damage.js';
import { performContestRetry } from '../ability/statuses/retry-contest-on-damage.js';
import { applyMemberStatusRoots, type MemberStatusStep } from '../compile/member-status-recompile.js';
import { selectSkillForContestingRoll } from '../ability/select-skill-for-contesting-roll.js';
import { skillRollArr } from '../ability/skill-roll.js';
import { SKILL_ROLL_TYPE } from '../traps/skill-check.js';
import { consentPending, TRIGGER_CONSENT_REASON } from '../ability/trigger-consent.js';
import { isDormantOwnerGatedAura, AuraOwnerGateUnavailable, AURA_OWNER_STATE_FREE_LAYERS, STATUS_GRANT_LAYERS } from '../compile/creature-status-reader.js';
import { performTimedFinish, timedActivityFamily, type TimedFinishInput, type TimedFinishOutcome } from '../ability/timed-finish.js';
import { resolveChargedSource, sourceItemType } from '../ability/charged-cast.js';
import { isOperableObject, itemTypeGateAdmits, OPERABLE_OBJECT_LAYER } from '../../shared/ability-targeting-contract';
import { selectionVisibilityVerdict, usesCasterInventoryPicker } from './cast-range-gate.js';
import { frameCopyOnRead, frameSnapshot, unreadSourceRow, type FrameCopy } from '../ability/frame-snapshot.js';
import { collectTriggerCandidates, gateTriggerCandidate, injectUniversalOpportunityAttack } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { consumeShielded } from '../ability/statuses/consume-shielded.js';
import { EXTRA_DAMAGE_ROLL_TYPES, isStatusPassiveExtraDamageLayer } from '../ability/rolls/extra-damage-layers.js';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers.js';
import { statusScalar } from './activity.js';
import { applyUniversalFortunateOnLuckGain } from '../ability/reaction-inventory.js';
import { postRollReactionsPreDraw, runPostRollRetaliation } from '../ability/post-roll-reactions.js';
import { runK4OnHit } from '../ability/k4-dispatch.js';
import { type SummonPlan, type SummonPoint, SUMMON_POINTS_MAX, summonPointTarget, validSummonPoint } from '../ability/summon.js';
import { skipUnported, unportedSkipRow } from '../unported.js';
import { extraordinaryAuthorize, spendExtraordinary, type ExtraordinaryCast } from '../sanguine/extraordinary-plan-21.js';

type Row = Record<string, unknown>;

export const ABILITY_USE_SCHEMA = 'janus.action.ability.v2' as const;
export type { LogRow as AbilityLogRow, StatusRow as AbilityStatusRow } from '../ability/context.js';

export interface AbilityUseInput {
  hunkerObject?: HunkerObject | null;
  extraordinary?: ExtraordinaryCast;
  encounterId: string;
  casterId: string;
  targetIds: string[];
  placed?: PlacedAoePlacement;
  abilityId: string;
  rawDiceMod?: number;
  rawDiceAdvDisStacks?: number;
  useAbilityVariable1?: unknown;
  useAbilityVariable2?: unknown;
  consumeInventoryId?: string;
  consumeInventoryUnit?: true;
  /** The initiating cast's bound projectile inventory instance, never one of its affected targets. */
  hurlInventoryId?: string;
  throwLob?: 0 | 1;
  rollPrivate?: number;
  /** lane A9: the RAW target_id selection string the page would post (standard_inputs_v2 → status_casting_targets_arr) — a timed cast's
   *  start stores it for its finish. A placed cast's is its placement wire. Absent = ''. */
  selection?: string;
  /** The gate's verdict is already known (the oracle replays a cast PHP's gate allowed after the browser gate stood down). */
  skipGate?: boolean;
  /** The caller's commit stores form inventory writes; movement/fall transports leave this absent. */
  persistShapeshiftInventory?: boolean;
  /** An INSTANT PROJECTILE VOLLEY (use_ability_inc.php ~L196–318, lane RX 2026-09-28): `abilityId` is the parent spell, cast at the
   *  caster; then `childId` is dispatched once per creature selection or placed area, in order. */
  volley?: { childId: string; selections: string[]; areas?: PlacedAoePlacement[] };
  /** A REAL FALL (Session A step 9): check_and_apply_falling's unsupported tail for `casterId` (the faller) falling `fallFt` feet —
   *  `abilityId` is the Falling Damage ability, `targetIds` the faller; an engine consequence of a move, so no cast gate runs
   *  (ability/movement/fall-damage.ts). Committed through the ability commit's `fall` block. */
  fall?: { fallFt: number };
  /** A MOVE'S TRIGGERS (Session A step 11): final_creature_move's trigger tail for `casterId` (the mover, standing at its landing) —
   *  `onMove`: janus_trigger('triggers_on_move') on the mover (self targets, no inciting target); `entered`: each watcher whose reach the
   *  mover entered, janus_trigger('triggers_when_target_enters_reach') with the mover as inciting target and frame. `abilityId` and
   *  `targetIds` are ignored. An engine consequence of a move: no cast gate. Committed through the ability commit's `move_trigger` block. */
  moveTriggers?: { onMove: boolean; entered: string[]; watchers?: Array<{ id: string; trigger: string }> };
  /** Wizard v9 WP3: the FT3D SUMMON placement of this cast — the ground point it aims at and the spots banked one per summon. `targetIds` is
   *  then empty: the cast resolves on that point (ability/summon.ts summonPointTarget), as PHP's encounter-blob target. */
  summon?: { point: SummonPoint; points?: SummonPoint[]; pick?: string };
}

export type AbilityUseReaders = AbilityPreconditionReaders & JanusReaders & IceWallDisplacementReaders & { placedAoe?: PlacedAoeReaders;
  /** A page-observed delayed finish rejudges the current directed target. Undefined is unproved, never allow. */
  timedFinishTargetFailure?: (caster: Row, target: Row, ability: Row) => string | null | undefined };

export type AbilityUseVerdict =
  | {
    kind: 'resolved';
    warnings?: string[];
    statusTriggerListeners?: import('../ability/context.js').StatusTriggerListener[];
    rows: LogRow[];
    areaCasts?: import('../../shared/magic-area.js').AreaCastVfx[];
    teleportOffers?: import('../ability/movement/ability-teleport.js').AbilityTeleportOffer[];
    /** encounter_creature_id → column → value AFTER the use (only the columns that changed). */
    ec: Record<string, Record<string, unknown>>;
    statusesAdded: StatusRow[];
    /** status_item_id + target of every status row the use removed. */
    statusesRemoved: JanusCtx['statusesRemoved'];
    charges: Array<{ holder: string; item_id: string; before: number; charges: number }>;
    chargesRemoved?: NonNullable<JanusCtx['chargesRemoved']>;
    playerCharactersChanged: Array<{ item_id: string; skill_id: string; column: string; before: number; after: number }>;
    inventory: InventoryChange[];
    weaponInventoryChanges?: import('./weapon-attack-inventory.js').WeaponInventoryChange[];
    timedInventory?: import('../ability/timed-finish/inventory.js').TimedInventoryDelta;
    monsterBitRemovals?: import('../inventory/monster-bit-removal.js').MonsterBitRemoval[];
    shapeshiftInventory?: import('../statuses/shapeshift-inventory.js').ShapeshiftInventoryWrite[];
    /** A CREATURE lethal hit the browser stopped one call short of the legacy death functions on — see
     * ability/context.ts's JanusCtx.deathTail. null when the use killed nobody (the ordinary case). */
    deathTail: DeathTailEntry[];
    dyingTail?: JanusCtx['dyingTail'];
    /** Historical discovery descriptors remain empty; the browser already supplies exact status removals. */
    concentrationBreaks: ConcentrationBreak[];
    /** Quantity-family increments on EXISTING status rows (janus_add_status_stack) — ability/context.ts StatusStackChange. */
    statusStacks: StatusStackChange[];
    /** Lane RX: the armed counterspell watches this use claimed (commit re-claims them atomically) — absent when none was examined. */
    counterspell?: CounterspellClaims;
    counterspellArm?: JanusCtx['counterspellArm'];
    /** Wizard v9 spells_cannot_be_counterspelled: the spell is protected, so the armed watches were not examined (none claimed). */
    counterspellExempt?: true;
    /** Session A2 step 4c: the turn boundary a lethal hit on the turn holder makes — ability/context.ts JanusCtx.turnTransition. */
    turnTransition?: TurnDeathTransition;
    /** Browser projection of the transition already saved by the typed spawn row plan. */
    spawnTurnTransition?: TurnDeathTransition;
    /** …or the holder it killed when the boundary decided no advance (fewer than two combatants). */
    turnDeathNone?: string;
    /** OWNER RULING 2026-10-03: the player-controlled members whose luck gain was NOT spent on Fortunate inline — offered after the commit
     *  (ability/trigger-consent.ts deferFortunateOffer). A page-side field: the page strips it before posting. */
    fortunateOffers?: string[];
    nextSpellOpportunityOffers?: string[];
    /** Wizard v9 WP3: the summons this use decided (ability/summon.ts) — persisted by the ability commit's `summons` block. */
    summons?: SummonPlan[];
    spawnRows?: import('../spawn/row-plan').SpawnRowPlan;
    /** Wizard v9 WP5: the drained source becomes a fixed quantity/status/log plan in ability-commit-frame. */
    chargedConsume?: { inventoryId: string };
    /** Browser-owned presentation; the commit only attaches persisted routing. */
    cueContexts?: Row[];
    spawnLogRows?: LogRow[];
    forcedMovements?: ForcedMovement[];
  }
  | { kind: 'refuse'; message: string }
  | { kind: 'handback'; reason: string; needs?: InventoryDamageNeed[]; playerMessage?: string };

/** Dispatch borrows authored definitions. queueNextSpellTarget alone appends to the
 * prepared plan; own that path per trial/projectile and retain its read-only carriers.
 * Other cast overrides (including fall dice) replace their own branches. */
function dispatchDefinition(definition: Row): Row {
  const plan = nextSpellPlan(definition);
  return plan ? { ...definition, computed: { ...record(definition.computed), // dnr-clone: allow size=4 shallow containers (root, computed, plan, target list); why=own only the appended target path; authored branches and carriers retain identity
    next_spell: { ...plan, targets: [...plan.targets] } } } : definition;
}
/** Each synchronous decision reads the held inputs through an isolated field view. Catalogs are read-only;
 * runs own their writes. Encounter roster rows and explicit members remain separate, as in the original cast frame. */
function useCopies(encounter: Row, members: ReadonlyMap<string, Row>): { encounter: () => Row; members: () => Map<string, Row> } {
  // A member whose stored status bucket arrived as a PHP list OBJECT gets a real list on ITS COPY, so the engine's list writes see every row.
  const listObjects = new Set([...members].filter(([, row]) => statusListObjectSides(row).length).map(([id]) => upper(id)));
  return {
    encounter: () => frameCopyOnRead(encounter, new Map(), { catalogs: 'share' }).encounter,
    members: () => {
      const copies = frameCopyOnRead({}, members, { catalogs: 'share' }).members;
      for (const id of listObjects) {
        const row = copies.get(id)!;
        const statuses = record(row.statuses)!;
        for (const side of statusListObjectSides(row)) statuses[side] = statusBucketRows(statuses[side]);
      }
      return copies;
    },
  };
}
/** A stored status bucket as its rows: a list, or a PHP list json_encode emitted as an OBJECT (every key numeric — its keys went out of order;
 *  apply_encumbrance_status_effects_to_item_arr.php's hold-aside, fixed 2026-10-16) in key order. Anything else is not a list: null. */
function statusBucketRows(raw: unknown): unknown[] | null {
  if (Array.isArray(raw)) return raw;
  if (raw === null || typeof raw !== 'object') return null;
  const keys = Object.keys(raw);
  return keys.every((key) => /^\d+$/.test(key)) ? keys.sort((a, b) => Number(a) - Number(b)).map((key) => (raw as Row)[key]) : null;
}
function statusListObjectSides(row: Row): Array<'target' | 'caster'> {
  const statuses = record(row.statuses);
  return statuses ? (['target', 'caster'] as const).filter((side) => !Array.isArray(statuses[side]) && statusBucketRows(statuses[side]) !== null) : [];
}

/** R-116 (owner 2026-09-29, Priest Martyr Eternal; lane A11 per N4f 2026-10-03): an `if_aura_owner_has_status_effect_active` aura on a member.
 *  The owner is resolved from the roster the page holds (the members, then the encounter's roster) — `resolveOwner`, so
 *  isDormantOwnerGatedAura DECIDES (a dead / deleted owner → dormant, PHP ft_aura_owner_state) and never throws for want of a roster; an
 *  owner the page's roster does not hold at all hands back (PHP reads it from the DB in any encounter — not decidable here). The use then runs on the holder's cached blob only when that blob is exactly what the owner's CURRENT state compiles:
 *  - grants, foreign origins and compiled payload outside the skill_roll_* lists still need a live rebuild;
 *    status extra-damage fragments use their owner's current gate directly at damage time;
 *  - the skill_roll_* lines (compile/aura-owner-skill-lines.ts: one {href_id: <row>} line per targeted skill while ACTIVE, none while dormant)
 *    must agree with the judged state → else hand back `aura-owner-lines-stale` (the owner's vitality moved since the holder's last bake).
 *  null = judged, the use may run. A state the owner row cannot answer (no v_max, unheld statuses) hands back by the gate's own reason. */
export function ownerGatedAuraGate(member: Row, status: Row, statusDef: Row, members: ReadonlyMap<string, Row>, encounter: Row,
    read: AbilityUseReaders): string | null {
  const embedded = { ...status, status_arr: statusDef };
  const origin = upper(status.status_origin_id), item = upper(status.status_item_id ?? itemField(statusDef, 'item_id'));
  if (origin !== '' && origin !== item && !isSelfStatusOrigin(upper(status.status_id), origin)) return 'creature_payload_statuses:aura-owner-live-payload-rebuild';
  const layers = Object.keys(record(statusDef.item_layer_types) ?? {});
  const damagePassive = itemField(statusDef, 'item_type') === 'passive_ability'
    && EXTRA_DAMAGE_ROLL_TYPES.includes(upper(read.value(statusDef, 'roll_type')));
  if (STATUS_GRANT_LAYERS.some((l) => read.has(statusDef, l))
      || layers.some((l) => !AURA_OWNER_STATE_FREE_LAYERS.has(l) && !SKILL_ROLL_LAYERS.includes(l)
        && !(damagePassive && isStatusPassiveExtraDamageLayer(l)
          && !['weapon_attack_mod', 'bright_light_dim_light', 'enchantment_weapon_type'].includes(l))))
    return 'creature_payload_statuses:aura-owner-live-payload-rebuild';
  const roster = record(encounter.encounter_creatures_arr) ?? (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : null);
  const resolveOwner = (id: string): Row | null => members.get(upper(id))
    ?? (Object.values(roster ?? {}).map(record).find((r) => upper(record(r?.encounter_creatures)?.encounter_creature_id) === upper(id)) ?? null);
  // PHP's ft_aura_owner_state reads the owner's row from the DB in ANY encounter: an owner this page's roster does not hold is undecidable here
  // (the helper's "absent → dormant" would be a guess) → hand back by the gate's own reason, never assume dormancy
  const ownerId = upper(status.status_caster_id);
  if (/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(ownerId) && !resolveOwner(ownerId))
    return 'creature_payload_statuses:aura-owner-state-not-held';
  let dormant: boolean;
  try { dormant = isDormantOwnerGatedAura(embedded, (row, key) => read.value(row as Row, key), resolveOwner, member); }
  catch (error) { if (error instanceof AuraOwnerGateUnavailable) return error.reason; throw error; }
  // a skill_roll_mod payload (a zero mod total stores no breakdown — its lines can hide) is not proven from the blob → hand back
  if (read.has(statusDef, 'skill_roll_mod')) return 'creature_payload_statuses:aura-owner-live-payload-rebuild';
  const rowId = upper(status.status_id);
  const skills = record(record(record(member.computed)?.creature)?.skills) ?? {};
  // every cached line this status row contributes, per SKILL (the skills map's variable key), as its value (a mod-breakdown line never agrees)
  const carried = new Map<string, number[]>();
  for (const [variable, s] of Object.entries(skills)) for (const b of ['adv_dis_breakdown', 'skill_roll_mod_breakdown']) {
    const lines = record(record(s)?.[b])?.lines;
    if (Array.isArray(lines)) for (const l of lines) if (upper(record(l)?.href_id) === rowId)
      carried.set(variable.toLowerCase(), [...(carried.get(variable.toLowerCase()) ?? []), b === 'adv_dis_breakdown' ? Number(record(l)?.value) : NaN]);
  }
  // (A11 Codex review Q5 P1/P2, Q6 P2) COMPLETE agreement per skill, not presence: dormant (or no skill payload) → no line at all; ACTIVE →
  // exactly one +1 line per skill_roll_adv entry and one −1 per skill_roll_dis entry ON the skill the entry names (aura-owner-skill-lines.ts;
  // an entry is a skill id — its definition's item_name, snake-cased, is the skills key, as check_ascension_skill_point_gain reads it — or the
  // variable name itself); an entry this page cannot place, or any other count / value / skill, hands back
  const want = new Map<string, number[]>();
  if (!dormant) for (const [layer, value] of [['skill_roll_adv', 1], ['skill_roll_dis', -1]] as const) {
    const slot = record(statusDef.item_layer_types)?.[layer];
    const entries = Array.isArray(slot) ? slot : record(slot) ? Object.values(slot as object) : slot == null || slot === '' ? [] : [slot];
    for (const entry of entries) {
      const raw = typeof entry === 'string' ? entry : record(entry) ? (record(entry)!.item_layer_effect ?? record(entry)!.item_id ?? record(record(entry)!.items)?.item_id ?? record(entry)!.variable_name ?? record(entry)![0]) : entry;
      const token = String(raw ?? '').trim().split('|', 1)[0] ?? '';
      let variable = token.toLowerCase();
      if (/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(token)) {
        const skillDef = read.definition(upper(token));
        if (!skillDef) return 'creature_payload_statuses:aura-owner-skill-not-held';
        variable = String(itemField(skillDef, 'item_name') ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      }
      if (!variable) return 'creature_payload_statuses:aura-owner-skill-not-held';
      want.set(variable, [...(want.get(variable) ?? []), value]);
    }
  }
  const norm = (m: Map<string, number[]>): string => [...m].map(([k, v]) => `${k}:${[...v].sort((x, y) => x - y).join(',')}`).sort().join(';');
  return norm(carried) === norm(want) ? null : 'creature_payload_statuses:aura-owner-lines-stale';
}
const SKILL_ROLL_LAYERS: readonly string[] = ['skill_roll_adv', 'skill_roll_dis', 'skill_roll_mod'];
/** Extreme-dice branches: all max, all min, and max/min alternating in runs of 1, 2 and 3 draws (either phase) — a contest's
 * skill die and an ADV/DIS second d20 shift which draw belongs to which side, so a period-1 alternation alone misses outcomes
 * (Codex review 2026-09-27: Spiral of Light reached push_ft_after_hit after five real draws). */
const DRY_RUN_PATTERNS = ['max', 'min', 'alt1-hi', 'alt1-lo', 'alt2-hi', 'alt2-lo', 'alt3-hi', 'alt3-lo'] as const;
/** Outcome-gated hand-backs the dry run does NOT hoist yet (each a documented residual bias in the handoff):
 *  · a second natural-20 luck gain by one creature in one use (the first gain's Fortunate is live) — ~0.25% of such uses.
 * (The ranged natural-1 stray shot is ported — stray-shot.ts — and its candidates are dry-run below.) */
const DRY_RUN_RESIDUALS = new Set(['caster_gains_luck:second-gain-this-use']);
/** DRY-RUN DEDUPE (fix-bug 2026-10-14): a dry run is a pure function of its inputs (one serialization, fresh copies) and the faces it draws,
 *  and what it leaves in state shared across runs is idempotent (randomTableSeen, the consent asks). A pattern making the same high / low
 *  choice at every draw an earlier FINISHED run of the same inputs consumed (same forced random-table outcome) replays that run exactly, so it
 *  is skipped. A start drew no die in 216 of 504 frame dry runs: all eight patterns were one run. */
type DryRunTrace = { key: string; choices: boolean[] };
const dryRunTwin = (done: readonly DryRunTrace[], key: string, pattern: (typeof DRY_RUN_PATTERNS)[number]): boolean =>
  done.some(run => run.key === key && run.choices.every((high, n) => patternHigh(pattern, n) === high));
/** The most whole-use dry runs the random-table outcome sweep may add (2 per outcome; the Short-Term Madness table hands out 131). */
const MAX_FORCED_TABLE_RUNS = 320;
const SPELL_RANGED_ATTACK = 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C';
/** Recorder shape gaps are proved on every synthetic frame before any live draw. */
/** apply_player_dying()'s cascade — Crippled, Dying, Maimed — admitted by the commit on a PC the verdict drops to 0 vitality. */
export const PC_DYING_STATUS_IDS = new Set(['99C862B9-D6AE-4E18-7803-A6F9B42A88E1', '8AE129AF-52D8-CCE8-706A-85767DB52740', 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D']);
export function commitShapeGate(ctx: JanusCtx, { assignStatusIds = true } = {}): void {
  if (ctx.summons?.length && !ctx.read.spawnSummons) throw new HandBack('spawn:source-package-not-prepared');
  if (assignStatusIds && ctx.summons?.length && ctx.read.spawnSummons) ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, ctx.read.spawnSummons(ctx, ctx.summons));
  if (assignStatusIds) assignFreshStatusIds(ctx);
  if (ctx.inventoryChanged.length > 128) throw new HandBack('commit:inventory-too-many-changes');
  for (const change of ctx.inventoryChanged)
    if (Object.keys(change.changes).some(key => !['v_current', 'v_temp', 'v_max'].includes(key))) throw new HandBack('commit:inventory-delta');
  for (const change of ctx.inventoryChanged) if (change.movement
    && (!Object.keys(change.movement).every(key => ['x_pos', 'y_pos', 'z_pos'].includes(key))
      || !Object.values(change.movement).every(Number.isFinite))) throw new HandBack('commit:inventory-movement');
  assertMonsterBitRemovals(ctx.monsterBitRemovals ?? []);
  if (ctx.rows.some((row) => Array.isArray(row.rolls_full) && row.rolls_full.length > 256)) throw new HandBack('commit:rolls-full-too-long');
  for (const row of ctx.rows) for (const face of Array.isArray(row.rolls_full) ? row.rolls_full : [])
    if (Array.isArray(face) && !commitFaceAccepted(face[0])) throw new HandBack('commit:face-not-accepted');
  if (!ctx.composedDeaths?.length && ctx.rows.some((r) => ['creature_killed', 'massive_damage_instant_death', 'creature_killed_forever'].includes(String(r.roll_type_1))))
    throw new HandBack('commit:death-class-row');
  for (const status of ctx.statusesAdded) {
    const definition = ctx.read.definition(upper(status.status_item_id));
    // the id rides the reason so ability-truth.ts fetches it and resolves again (pre-draw — lane PC1 2026-10-09: Monstrous Transformation's
    // "Spent Until Combat Ends" lockout row handed every cast back to the legacy route when the page had never held the helper passive)
    const failure = statusCapabilityFailure(definition, status.status_item_id);
    if (failure) throw new HandBack(failure);
  }
}
function abilityFrameReaders(read: AbilityUseReaders): AbilityUseReaders {
  return { ...read, prepareIceWallDisplacements: read.prepareIceWallDisplacements ?? prepareIceWallDisplacements,
    killTriggerProofs: read.killTriggerProofs ?? new Map(),
    killTriggerRecorder: read.killTriggerRecorder ?? (ctx => {
      return () => {
        commitShapeGate(ctx, { assignStatusIds: false }); return null;
      };
    }) };
}
/** The high / low choice `pattern` makes at its draw `n` (0-based). */
function patternHigh(pattern: (typeof DRY_RUN_PATTERNS)[number], n: number): boolean {
  if (pattern === 'max' || pattern === 'min') return pattern === 'max';
  const firstRun = Math.floor(n / Number(pattern[3])) % 2 === 0;
  return pattern.endsWith('-hi') ? firstRun : !firstRun;
}
/** `choices` (optional) records the high / low choice of every draw the run consumes — its DryRunTrace. */
export function syntheticRollSource(pattern: (typeof DRY_RUN_PATTERNS)[number], choices?: boolean[]): RollSource {
  let n = 0;
  const high = (): boolean => { const h = patternHigh(pattern, n); choices?.push(h); return h; };
  return {
    d(die: number): number { const face = high() ? die : 1; n += 1; return face; },
    fraction(): number { const value = high() ? 0.999999 : 0; n += 1; return value; },
  };
}
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
const DOUBLE_CONCENTRATION = '45122975-6514-4CE0-9484-A874CE211016';

function directReactionRefusal(caster: Row, ability: Row, encounter: Row, read: AbilityUseReaders): string | null {
  if (!read.inCombat(encounter, caster)) return null;
  const type = String(itemField(ability, 'item_type') ?? '');
  const castingTime = String(read.value(ability, 'spell_casting_time') ?? '');
  const reactionSpell = ['spell', 'curse', 'menta'].includes(type) && castingTime === 'reaction';
  if (type !== 'reaction_ability' && type !== 'opportunity_attack_ability' && !reactionSpell) return null;
  const reaction = ecLean(caster, 'reaction');
  if (reaction == null) throw new HandBack('use_ability_pre_conditions:reaction-not-held');
  if (requiredInt(reaction, 'use_ability_pre_conditions:invalid-reaction') > 0) return null;
  return reactionSpell ? 'You have already used your reaction, and cannot cast this spell.'
    : 'You have already used your reaction this round.';
}

function concentrationRefusal(caster: Row, ability: Row, read: AbilityUseReaders): string | null {
  if (!read.has(ability, 'spell_concentration')) return null;
  const count = read.statuses(caster).reduce((total, s) => {
    if (upper(s.status_item_id ?? itemField(record(s.status_arr), 'item_id')) !== CONCENTRATING) return total;
    const stack = requiredInt(s.status_stack === undefined ? 1 : s.status_stack, 'use_ability_pre_conditions:concentration-stack-not-modeled');
    if (stack !== 1) throw new HandBack('use_ability_pre_conditions:concentration-stack-not-modeled');
    return total + 1;
  }, 0);
  if (!count) return null;
  if (!read.resolveItem) throw new HandBack('use_ability_pre_conditions:concentration-feat-reader-not-held');
  const bifocus = casterHoldsPassive(caster, DOUBLE_CONCENTRATION, { entries: read.entries, resolveItem: read.resolveItem });
  if (bifocus === null) throw new HandBack('use_ability_pre_conditions:concentration-passive-not-held');
  if (count < (bifocus ? 2 : 1)) return null;
  return `You are already concentrating on ${count} ${count === 1 ? 'spell' : 'spells'}(s) or abilities. End concentration as a reaction before using this ability.`;
}

/** A status an earlier projectile left that could change a LATER projectile's numbers. PHP re-reads and RECOMPILES the target before
 *  every projectile (separate janus() calls), while the browser pipeline reads compiled values as of the use's start and applies the
 *  single-dispatch "pending" rules — so any rider that can move a roll, a modifier, an essence, AC, vitality or luck hands back.
 *  Volley oracle evidence (tools/test/artifacts/janus-rx-volley-oracle-2026-09-28): a pending Fortunate (Force Bolts AO-0-combat —
 *  ADV on the next contest) and the Dying cascade's Crippled / Maimed (Scorching Ray AO-1-lethal — a skill modifier −1). */
function volleyRollAffecting(read: AbilityUseReaders, statusItemId: string): boolean {
  if (statusItemId === 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8') return true; // Fortunate
  const definition = read.definition(statusItemId);
  if (!definition) return true;
  // The families PHP FOLDS INTO THE COMPILE (DynamicLayer::LIST — skill / contest / attack mods and ADV, AC, essences, senses, speeds …)
  // are read from computed.creature and refreshed at the projectile boundary; roll-time families (roll_type_adv/dis, the against_*
  // pair — Crippled, Prone, Blinded) are read from the status list, which carries this use's rows. Volley oracle: evidence-driven.
  // All placements and selection visibility were proved before the volley. Light
  // alone (Burning) cannot change a later impact's rolls or object damage.
  return Object.keys(record(definition.item_layer_types) ?? {}).some((key) => key !== 'bright_light_dim_light' && PASSIVE_DYNAMIC_LAYERS.has(key));
}

const ON_MOVE = { trigger: 'triggers_on_move' } as const;
const ENTERS_REACH = { trigger: 'triggers_when_target_enters_reach' } as const;
/** final_creature_move.php's trigger tail (L661–784, Session A step 11): the mover's triggers_on_move, then each entered watcher's
 *  triggers_when_target_enters_reach — the janus_trigger twin (reactions/trigger-dispatch.ts) through the in-pipeline executor, exactly as
 *  a reaction inside an ordinary use runs. Every hand-back is decided before the real draw (the pre-draw preview, then the extreme dry runs). */
function resolveMoveTriggers(input: AbilityUseInput, spec: NonNullable<AbilityUseInput['moveTriggers']>, encounter: Row, members: ReadonlyMap<string, Row>,
  read: AbilityUseReaders, roll: RollSource): AbilityUseVerdict {
  read = abilityFrameReaders(read);
  const moverId = upper(input.casterId);
  const entered = [...new Set(spec.entered.map(upper))].sort(); // PHP's sorted watcher list order
  const watchers = [...entered.map(id => ({ id, trigger: ENTERS_REACH.trigger })), ...(spec.watchers ?? [])];
  if (!spec.onMove && !watchers.length) return { kind: 'handback', reason: 'move_triggers:empty' };
  if (watchers.some(({ id }) => upper(id) === moverId || !members.has(upper(id)))) return { kind: 'handback', reason: 'move_triggers:watcher-not-held' };
  const copies = useCopies(encounter, members);
  const fresh = (r: RollSource): JanusCtx => ({ encounter: copies.encounter(), members: copies.members(),
    originals: members, read: abilityFrameReaders(read), roll: r, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
    statusConnectedId: null, rollPrivate: input.rollPrivate ?? 0, deathTail: null, concentrationBreaks: null, statusStacks: [], teleportOffers: [] });
  const moverOf = (c: JanusCtx): Row => c.members.get(moverId) as Row;
  const watcherOf = (c: JanusCtx, id: string, trigger: string): Row => {
    const owner = c.members.get(upper(id)) as Row;
    if (trigger !== 'triggers_when_target_leaves_reach') return owner;
    const injected = injectUniversalOpportunityAttack(janusCtxTriggerReaders(c), owner);
    c.members.set(upper(id), injected);
    return injected;
  };
  const perform = (c: JanusCtx): void => {
    if (spec.onMove) runTriggerFamily(c, ON_MOVE, moverOf(c), null, [moverOf(c)], false, triggerExecutorGap, true);
    // PHP hands the enter dispatches the mover as it stands after its own triggers; a mover those triggers killed is not modelled
    if (entered.length && Number(record(moverOf(c).encounter_creatures)?.encounter_creature_dead ?? 0) === 1)
      throw new HandBack('move_triggers:mover-died-before-reach');
    for (const { id, trigger } of watchers) runTriggerFamily(c, { trigger }, watcherOf(c, id, trigger), moverOf(c), [moverOf(c)], false);
  };
  try {
    // the pre-draw preview on the state before any dispatch (every carrier runnable, what it reads held)
    const preview = fresh(roll);
    if (spec.onMove) triggerFamilyPreDraw(preview, moverOf(preview), ON_MOVE);
    for (const { id, trigger } of watchers) triggerFamilyPreDraw(preview, watcherOf(preview, id, trigger), { trigger }, triggerExecutorGap, moverOf(preview));
  } catch (error) {
    if (error instanceof HandBack) return { kind: 'handback', reason: `preflight:${error.reason}` };
    return { kind: 'handback', reason: `preflight:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
  }
  const moveDone: DryRunTrace[] = [];
  for (const pattern of DRY_RUN_PATTERNS) {
    if (dryRunTwin(moveDone, '', pattern)) continue;
    const choices: boolean[] = [];
    const dry: JanusCtx = { ...fresh(syntheticRollSource(pattern, choices)), dryRun: true };
    try { perform(dry); commitShapeGate(dry); }
    catch (error) {
      if (error instanceof HandBack && DRY_RUN_RESIDUALS.has(error.reason)) continue;
      if (error instanceof HandBack) return { kind: 'handback', reason: `preflight:${error.reason}` };
      if (error instanceof Refusal) return { kind: 'handback', reason: 'preflight:move_triggers:refusal' };
      return { kind: 'handback', reason: `preflight:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
    } finally { moveDone.push({ key: '', choices }); }
  }
  if (consentPending(read)) return { kind: 'handback', reason: `preflight:${TRIGGER_CONSENT_REASON}` }; // OWNER RULING 2026-10-02: ask first
  const ctx = fresh(roll);
  perform(ctx);
  if (consentPending(ctx.read)) return { kind: 'handback', reason: `outcome:${TRIGGER_CONSENT_REASON}` };
  commitShapeGate(ctx);
  if ('remaining' in roll && typeof roll.remaining === 'function' && roll.remaining() !== 0)
    return { kind: 'handback', reason: 'janus:roll-sequence-incomplete' };
  return resolvedVerdict(ctx);
}
/**
 * Resolve one ability use. `members` are the encounter's member rows as the page holds them (keyed upper-case id); they are NOT
 * mutated — the verdict carries the changes. Draws come ONLY from `roll`.
 */
export function resolveAbilityUse(
  input: AbilityUseInput,
  encounter: Row,
  members: ReadonlyMap<string, Row>,
  read: AbilityUseReaders,
  roll: RollSource,
): AbilityUseVerdict {
  const unavailable = (error: HandBack, prefix = ''): AbilityUseVerdict => ({ kind: 'handback', reason: prefix + error.reason,
    ...(error instanceof TriggerUnavailable ? { playerMessage: `${String(itemField(read.definition(input.abilityId), 'item_name') ?? 'This action')} can't resolve yet: ${error.playerMessage}` } : {}) });
  try {
  if (input.rawDiceMod !== undefined && !Number.isFinite(input.rawDiceMod))
    return { kind: 'handback', reason: 'janus:invalid-raw-dice-modifiers' };
  if (input.rawDiceAdvDisStacks !== undefined && !Number.isFinite(input.rawDiceAdvDisStacks))
    return { kind: 'handback', reason: 'janus:invalid-raw-dice-modifiers' };
  if (input.rollPrivate !== undefined && !Number.isSafeInteger(input.rollPrivate))
    return { kind: 'handback', reason: 'janus:invalid-roll-private' };
  const casterOriginal = members.get(upper(input.casterId));
  if (!casterOriginal) return { kind: 'handback', reason: 'caster-not-held' };
  if (upper(input.abilityId) === HUNKER_ABILITY) {
    const message = hunkerUnavailableReason(encounter, casterOriginal, input.hunkerObject, read.value);
    if (message) return { kind: 'refuse', message };
    input = { ...input, skipGate: false };
  }
  for (const member of members.values()) {
    // Owner 2026-09-29, Priest Martyr Eternal: cached skills/grants cannot model a live owner flip.
    // R-116 (lane A11, N4f ask 2026-10-03): the gate is JUDGED against the encounter roster (resolveOwner) instead of a blanket hand-back.
    for (const status of read.statuses(member)) {
      const statusDef = record(status.status_arr) ?? read.definition(upper(status.status_item_id));
      if (statusDef && read.has(statusDef, 'if_aura_owner_has_status_effect_active')) {
        const reason = ownerGatedAuraGate(member, status, statusDef, members, encounter, read);
        if (reason) return { kind: 'handback', reason };
      }
    }
    // The page's status reader must project every stored row (a list, or a PHP list object — statusBucketRows); a malformed bucket or row
    // the reader cannot see is not held.
    const raw = record(member.statuses)?.target;
    const stored = raw === undefined ? undefined : statusBucketRows(raw);
    if (raw !== undefined && (!stored || stored.some((status) => !record(status)) || read.statuses(member).length !== stored.length))
      return { kind: 'handback', reason: 'janus:status-projection-not-held' };
  }
  if (input.moveTriggers) return resolveMoveTriggers(input, input.moveTriggers, encounter, members, read, roll);
  let definition = read.definition(input.abilityId);
  if (!definition) return { kind: 'handback', reason: 'ability-definition-not-held' };
  const classAura = classAuraCastPrecheck(input, encounter, casterOriginal, definition, read);
  if (classAura.kind === 'refuse') return classAura;
  definition = classAura.definition;
  const extraordinary = input.extraordinary ? extraordinaryAuthorize(casterOriginal, input.abilityId, input.extraordinary, read) : null;
  // Dedicated choices still pass the ordinary mana, concentration and selection gates.
  if (extraordinary) input = { ...input, skipGate: false };

  // janus.php's direct reaction settlement precedes the ordinary ability gate (a FALL is not a cast — no gate of any kind).
  if (input.fall) input = { ...input, skipGate: true };
  const control = creatureControl(input.abilityId);
  const summonControl = [DISMISS_SUMMON, RECALL_SUMMON].includes(upper(input.abilityId));
  const actionCaster = summonControl ? members.get(upper(ecLean(casterOriginal, 'controller_encounter_creature_id'))) : casterOriginal;
  if (!actionCaster) return { kind: 'handback', reason: 'summon_control:controller-not-held' };
  const damageOnly = input.useAbilityVariable1 === 'damage_only';
  const reactionRefusal = input.fall || damageOnly ? null : directReactionRefusal(actionCaster, definition, encounter, read);
  if (reactionRefusal) return { kind: 'refuse', message: reactionRefusal };
  // A volley's projectile (the parent's single free_ability entry) is part of the spell the armed Sanguine modifies: the parent's plan is
  // judged against it too (Widened / Careful / Reverberation), the parent consumes the carriers once, and the volley block below rewrites
  // the projectile from that plan (prepareNextSpellProjectile). An unheld child is the volley block's own hand-back.
  const projectile = input.volley ? read.definition(input.volley.childId) : null;
  try { definition = prepareNextSpell(read, casterOriginal, definition, input.useAbilityVariable1, input.useAbilityVariable2, projectile); }
  catch (error) {
    if (error instanceof HandBack) throw new HandBack(`preflight:${error.reason}`);
    throw error;
  }

  // 1. The cast-time gate (use_ability_pre_conditions — the browser port, R-025).
  if (control && (input.fall || input.moveTriggers || input.volley || input.placed || input.summon || input.useAbilityVariable1 != null || input.useAbilityVariable2 != null))
    return { kind: 'handback', reason: 'summon_control:unsupported-frame' };
  const gateAbility = itemField(definition, 'item_type') === 'attack_ability'
    && (isVariableAmount({ read }, definition, input.useAbilityVariable1) || hasMoveTowardsTarget({ read }, definition))
    ? variableAttackComposite(read, definition, casterOriginal, encounter) : definition;
  const gate = (input.skipGate || damageOnly) && !control ? { kind: 'allow' as const } : abilityGateVerdict({ caster: casterOriginal, ability: gateAbility, encounter,
    actionCaster,
    // Object geometry was judged above; retain the normal turn/AP/action gate.
    ...(upper(input.abilityId) === HUNKER_ABILITY ? { sceneVerdicts: { hunker: { aloft: false, noCover: false } } } : {}),
    variable1: input.useAbilityVariable1, variable2: input.useAbilityVariable2, consumeInventoryId: input.consumeInventoryId,
    consumeInventoryUnit: input.consumeInventoryUnit, summonCreatureId: input.summon?.pick }, read);
  if (gate.kind === 'refuse') return { kind: 'refuse', message: gate.message };
  if (gate.kind === 'unknown') return { kind: 'handback', reason: `gate-unknown:${gate.site}` };
  definition = shapeThrowObjectCast(read, definition, casterOriginal, input.encounterId, input, encounter);
  if (isThrowObjectCast(definition, input) && !input.placed) return { kind: 'refuse', message: 'Choose where to throw it.' };
  if (!input.skipGate && !input.fall && !damageOnly) {
    const concentration = concentrationRefusal(actionCaster, definition, read);
    if (concentration) return { kind: 'refuse', message: concentration };
  }
  // A CHARGED-ITEM cast (Wizard v9 WP5, charged-cast.ts): use_ability_inc.php's discern_item_id(variable_2) — the source inventory row the
  // cast is paid from (wand / staff / scroll). A volley / fall / move frame never carries one.
  const charged = resolveChargedSource(read, input.useAbilityVariable1, input.useAbilityVariable2);
  if (charged && 'handback' in charged) return { kind: 'handback', reason: charged.handback };
  if (charged && (input.volley || input.fall || input.moveTriggers)) return { kind: 'handback', reason: 'charged_cast:engine-frame' };

  const copies = useCopies(encounter, members);
  const ctx: JanusCtx = {
    encounter: copies.encounter(),
    ...(input.persistShapeshiftInventory ? { shapeshiftInventory: [], shapeshiftActorId: upper(input.casterId) } : {}),
    members: copies.members(),
    originals: members,
    shapeshiftProjections: new WeakMap(),
    read: abilityFrameReaders(read),
    roll,
    rows: [],
    teleportOffers: [],
    statusesAdded: [],
    statusesRemoved: [],
    chargesChanged: [],
    inventoryChanged: [],
    statusConnectedId: null,
    rollPrivate: input.rollPrivate ?? 0,
    deathTail: null,
    concentrationBreaks: null,
    statusStacks: [],
    ...(charged ? { chargedSource: charged } : {}),
  };
  const caster = ctx.members.get(upper(input.casterId)) as Row;
  if (input.fall) {
    // A FALL is not a cast: none of the gates above apply to it (they ran only because a player's use is the ordinary input); the same
    // pre-draw fairness does — every extreme branch is resolved first, and any hand-back is decided before the real draw.
    if (upper(input.abilityId) !== FALLING_DAMAGE_ID || input.targetIds.length !== 1 || upper(input.targetIds[0]) !== upper(input.casterId))
      return { kind: 'handback', reason: 'check_and_apply_falling:input-shape' };
    const fallFt = input.fall.fallFt;
    const fallInput = { casterId: upper(input.casterId), targetIds: [upper(input.casterId)], abilityId: FALLING_DAMAGE_ID, rawDiceMod: 0,
      rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: upper(input.casterId) };
    const performFall = (c: JanusCtx): void => resolveFallDamage(c, fallInput, dispatchDefinition(definition), c.members.get(upper(input.casterId)) as Row, fallFt);
    const fallDone: DryRunTrace[] = [];
    for (const pattern of DRY_RUN_PATTERNS) {
      if (dryRunTwin(fallDone, '', pattern)) continue;
      const choices: boolean[] = [];
      const dry: JanusCtx = { ...ctx, encounter: copies.encounter(), members: copies.members(),
        roll: syntheticRollSource(pattern, choices), rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
        playerCharactersChanged: undefined, statusConnectedId: null, deathTail: null, concentrationBreaks: null, statusStacks: [], dryRun: true };
      try { performFall(dry); commitShapeGate(dry); }
      catch (error) {
        if (error instanceof HandBack && DRY_RUN_RESIDUALS.has(error.reason)) continue;
        if (error instanceof HandBack) return { kind: 'handback', reason: `preflight:${error.reason}` };
        if (error instanceof Refusal) continue;
        return { kind: 'handback', reason: `preflight:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
      } finally { fallDone.push({ key: '', choices }); }
    }
    if (consentPending(read)) return { kind: 'handback', reason: `preflight:${TRIGGER_CONSENT_REASON}` }; // OWNER RULING 2026-10-02: ask first
    performFall(ctx);
    if (consentPending(ctx.read)) return { kind: 'handback', reason: `outcome:${TRIGGER_CONSENT_REASON}` };
    commitShapeGate(ctx);
    if ('remaining' in roll && typeof roll.remaining === 'function' && roll.remaining() !== 0)
      return { kind: 'handback', reason: 'janus:roll-sequence-incomplete' };
    if (!ctx.rows.length) return { kind: 'handback', reason: 'check_and_apply_falling:no-fall' };
    return resolvedVerdict(ctx);
  }
  let targets: Row[] = [];
  // The instant parent casts at the caster, retaining its selected level/amount for the one-time cost.
  // The shared picker arithmetic budgets the ordered child assignments before any cost or die.
  const volley = input.volley ? (() => {
    if (input.placed) throw new HandBack('volley:placed-parent');
    const held = read.definition(input.volley!.childId);
    if (!held) definitionNotHeld('volley:child-definition-not-held', input.volley!.childId);
    // Under an armed Sanguine next-spell buff the projectile is rewritten from the parent's plan (prepareNextSpell above) BEFORE its areas
    // expand and before each projectile dispatch — the same rewrite-then-validate order a single placed spell gets. No buff → unchanged.
    const child = prepareNextSpellProjectile(read, definition, held);
    const areas = input.volley!.areas;
    if (read.has(child, 'aoe_type') ? !areas?.length || input.volley!.selections.length : areas?.length)
      throw new HandBack('volley:assignment-shape');
    // The assigned child must be the parent's single free_ability entry.
    const childEntries = read.entries(definition, 'free_ability').map(entry => upper(layerListEntryUuid(entry)));
    if (childEntries.length !== 1 || childEntries[0] !== upper(input.volley!.childId)) throw new HandBack('volley:child-not-the-parents');
    // Sanguine Boost (next_spell_upcast_levels, the parent's plan): the volley is cast at the BOOSTED level, so the pool grows exactly as a
    // level pick grows it — the sheet sizes the pool it offers the same way (action-bar.ts armInstantProjectileSpell) — while the mana
    // stays at the chosen level (janus.ts sanguine_paid_level). Unarmed, the chosen variable reaches the pool unchanged.
    const boost = nextSpellPlan(definition)?.boost ?? 0;
    const chosenLevel = /^\d{1,2}$/.test(String(input.useAbilityVariable1 ?? '').trim()) ? Number(input.useAbilityVariable1) : 0;
    const poolLevel = boost > 0 ? Math.max(Number(read.value(definition, 'spell_level')) || 0, chosenLevel) + boost : input.useAbilityVariable1;
    const pool = instantProjectileCount(itemField(definition, 'item_type'), key => read.value(definition, key),
      read.value(child, 'ability_charges'), poolLevel);
    if (pool === null) throw new HandBack('volley:pool-not-held');
    const count = areas?.length ?? input.volley!.selections.length;
    if (!count || count > pool) throw new HandBack('volley:selection-count');
    // ft_prepare_projectile_assignments() validates EVERY assignment first (validate_and_adjust_targets: range, sight, geometry) and
    // refuses the whole volley before any cost. The assignment UI judged range; sight is the selection rule (R-103) per assignment: an
    // unseen target refuses the volley, an unjudgeable read keeps it (the old "→ PHP" hand-back named every null read — a shapeless
    // scene, a paused runtime — "volley:target-not-visible").
    for (const selection of input.volley!.selections) {
      const sight = selectionVisibilityVerdict({ caster: casterOriginal, ability: child,
        targets: [{ id: selection, row: members.get(upper(selection)) ?? null }] }, read);
      if (sight.kind === 'refuse') throw new Refusal(sight.message);
    }
    return { childId: upper(input.volley!.childId), child, selections: input.volley!.selections.map(upper), areas };
  })() : null;
  if (volley) input = { ...input, targetIds: [upper(input.casterId)],
    useAbilityVariable2: input.useAbilityVariable2 === '' ? undefined : input.useAbilityVariable2 };
  // Carried picks keep their inventory identity on the same object lane as floor targets.
  const placedProps = new Map<string, Row>((Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : [])
    .map(record).filter((row): row is Row => row !== null).map(row => [inventoryId(row), row]));
  for (const id of new Set([...input.targetIds, ...(volley?.selections ?? [])].map(upper))) {
    if (members.has(id) || placedProps.has(id)) continue;
    for (const member of members.values()) {
      const target = cachedInventoryRow(member, id);
      if (target && Number(read.inventoryField?.(target, 'inventory_deleted') ?? 0) === 0) {
        placedProps.set(id, target);
        break;
      }
    }
  }
  const wallRunAoeSections = new Map<string, number[]>();
  // Prove every impact before the parent pays or draws. A creature shared by two areas belongs to both dispatches.
  // Each area expands from volley.child — already rewritten from the parent's Sanguine next-spell plan (Widened dimensions on its computed
  // aoe_* bridge, Careful's target_opposing_team_only), exactly as the single placed path below expands from the rewritten `definition`.
  const volleyAreas = volley?.areas?.map(placement => {
    if (!read.placedAoe) throw new HandBack('validate_and_adjust_targets:placed-readers-missing');
    const sections = new Map<string, number[]>();
    const expanded = finalizePlacedAoeTargets(read.placedAoe, placement, caster,
      applyAoeSparesAllies(read, caster, volley.child, 'validate_and_adjust_targets'), sections);
    for (const target of expanded) if (isInventoryRow(target)) placedProps.set(inventoryId(target), target);
    return { placement, targetIds: expanded.map(actorId), sections };
  });
  const volleyWallSections = new Map<string, number[]>();
  for (const area of volleyAreas ?? []) for (const [id, starts] of area.sections)
    volleyWallSections.set(id, [...new Set([...(volleyWallSections.get(id) ?? []), ...starts])]);
  let paintedWall: import('../ability/janus.js').JanusInput['paintedWall'];
  if (input.placed && !input.placed.paintedWall && isSelfFollowAura(ctx, definition)
      && isClassAuraDefinition(definition, (row, key) => read.value(record(row), key))) {
    if (!read.placedAoe) throw new HandBack('validate_and_adjust_targets:placed-readers-missing');
    const failure = read.placedAoe.placementFailure(input.placed.targetId, caster, definition);
    if (failure === undefined) throw new HandBack('ft_aoe_placement_failure:scene-proof-missing');
    if (typeof failure === 'string') throw new Refusal(failure);
    // A confirmed Self class aura belongs to its carrier. Its native dispatch expands the held roster and promotes the carrier's
    // occupant into the anchor; the stage's map point/occupant list is placement proof, not an external follow-area selection.
    input = { ...input, placed: undefined, targetIds: [upper(input.casterId)], selection: upper(input.casterId) };
  }
  let finalIds = control ? [upper(input.casterId)] : input.placed ? (() => {
    if (!read.placedAoe) throw new HandBack('validate_and_adjust_targets:placed-readers-missing');
    // Wizard v9 aoe_spells_spare_allies: janus() stamps the cast item skip-same-team (team-skip-gate.ts); the expansion must read that
    // stamp too, so the caster and its allies never enter the target set (neutrals and props still do).
    // An upcast placed area grows by spell_upcast_increase_aoe_ft × the levels above (upcast.ts): the expansion reads the cast copy's
    // computed.aoe_distance stamp (the reader's per-cast AOE bridge), as the FT3D stage sized the placement it posted.
    const growth = upcastAreaGrowthFt(read, definition, input.useAbilityVariable1);
    const sized = growth > 0 ? { ...definition, computed: { ...record(definition.computed),
      aoe_distance: Number(read.value(definition, 'aoe_distance') ?? 0) + growth } } : definition;
    const expanded = finalizePlacedAoeTargets(read.placedAoe, input.placed, caster,
      applyAoeSparesAllies(read, caster, sized, 'validate_and_adjust_targets'), wallRunAoeSections);
    const geometry = paintedWallForPlacement(read.placedAoe, input.placed, upper(input.casterId), sized);
    if (geometry) paintedWall = { abilityId: upper(input.abilityId), geometry };
    // validate_and_adjust_targets §4: a PERSISTENT area may land on empty ground (Fog Cloud reaching nobody — playtest 2026-10-13) — the
    // zone is placed with no targets; an instant area too (owner ruling 27, 2026-10-14): janus() casts it at the ground and it pays its cost
    for (const target of expanded) if (isInventoryRow(target)) placedProps.set(inventoryId(target), target);
    return expanded.map(target => actorId(target));
  })() : input.targetIds.map(upper);
  for (const id of finalIds) {
    const t = ctx.members.get(upper(id)) ?? placedProps.get(upper(id));
    if (!t) return { kind: 'handback', reason: 'target-not-held' };
    targets.push(t);
  }
  targets = excludeHurlObjectTargets(definition,
    isThrowObjectCast(definition, input) ? input.useAbilityVariable2 : input.hurlInventoryId, targets, {
    value: read.value, inventoryField: (row, key) => record(row?.inventory)?.[key],
  });
  finalIds = targets.map(actorId);
  for (const target of targets) {
    if (placedProps.has(actorId(target))) continue;
    const kind = String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '');
    if (!['creature', 'player_character'].includes(kind)
      && !(kind === 'lair' && (input.placed || SKILL_ITEM_TYPES.has(String(itemField(definition, 'item_type') ?? '')))))
      return { kind: 'handback', reason: 'janus:non-creature-target' };
  }
  // validate_and_adjust_targets.php checks the authored target item types before effects.
  const allowedTypes = read.entries(definition, 'ability_works_only_against_item_type').map((entry) => String(itemField(record(entry), 'item_id') ?? entry));
  const skipWorksOnlyAgainst = ['B2D3417B-2700-11CB-E17D-38DECAF66F1A', '5B518ED7-4CEF-72BE-F20B-912598267714']
    .includes(upper(input.abilityId));
  // §5 directed visibility (~:1440-1490) runs BEFORE this item-type gate (~:1606) in PHP: obscured picks of a non-area cast are
  // dropped first, and a selection with nothing visible left is refused on sight, not on type (RX reactor oracle 2026-09-29:
  // Frostwind Blade / Ram the Gate). A touch-located or unknown pick hands back.
  let typeGated = targets;
  if (allowedTypes.length && !skipWorksOnlyAgainst && !input.placed
      && ![definition, record(definition.attack_ability_arr)].some((layer) => !!layer && (read.has(layer, 'aoe_type') || read.has(layer, 'persistent_aoe')))) {
    typeGated = [];
    let dropped = 0;
    // a caster-inventory-picker ability (its gate names carryable gear) judged even a floor pick by sight in PHP — not modelled here
    const pickerSight = usesCasterInventoryPicker(definition, read);
    for (const target of targets) {
      if (upper(ecLean(target, 'encounter_creature_id')) === upper(input.casterId)) { typeGated.push(target); continue; }
      // A placed OBJECT pick (a floor row — the Bombard Pilot a Vehicle names) is not sight-gated: the page's selection gate
      // (cast-range-gate.ts selectionVisibilityVerdict) judges creature picks only, and PHP's §5 kept every player pick it did not mark.
      if (!pickerSight && placedProps.has(actorId(target))) { typeGated.push(target); continue; }
      const state = read.visibility(caster, target);
      if (state === null) return { kind: 'refuse', message: visibilityUnavailableMessage(encounter, caster, target) };
      if (state === 'touch_located') return { kind: 'handback', reason: 'validate_and_adjust_targets:touch-located-selection' };
      if (state === 'visible') typeGated.push(target); else dropped++;
    }
    if (dropped > 0 && !typeGated.length) return { kind: 'refuse', message: 'You cannot target into an obscured area.' };
  }
  if (allowedTypes.length && !skipWorksOnlyAgainst) for (const target of typeGated) {
    // a floor row types as its catalog template (inventory_item_id), as PHP's instance-full item_field() did; a war_machine gate also
    // admits an operable object (the piano — ft_item_is_operable_object)
    const object = placedProps.has(actorId(target));
    const targetType = object ? sourceItemType(read, target) ?? '' : String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '');
    if (!targetType || !itemTypeGateAdmits(allowedTypes, targetType, object && isOperableObject(targetType, read.value(target, OPERABLE_OBJECT_LAYER)))) {
      const name = record(target.player_characters)?.player_name ?? ecLean(target, 'encounter_creature_name')
        ?? (object ? record(target.inventory)?.inventory_name : null) ?? itemField(target, 'item_name');
      if (!name) return { kind: 'handback', reason: 'validate_and_adjust_targets:target-name-not-held' };
      const abilityName = String(itemField(definition, 'item_name') ?? 'this ability');
      if (/[<&>"']/.test(abilityName)) return { kind: 'handback', reason: 'validate_and_adjust_targets:html-name' };
      const allowed = allowedTypes.map((s) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())).join(', ');
      return { kind: 'refuse', message: `You cannot use ${abilityName} on ${name}.This ability only works on: ${allowed}.` };
    }
  }
  // Wizard v9 WP3: a ground SUMMON placement (the cast point + its banked spots), shape-checked; it never rides a creature selection
  const summonRequest = input.summon ? (() => {
    const points = input.summon!.points ?? [];
    if (finalIds.length || input.placed || input.volley || !validSummonPoint(input.summon!.point) || !Array.isArray(points)
      || points.length > SUMMON_POINTS_MAX || !points.every(validSummonPoint)) throw new HandBack('summon:placement-shape');
    const pick = typeof input.summon!.pick === 'string' && input.summon!.pick !== '' ? upper(input.summon!.pick) : undefined;
    return { point: input.summon!.point, points, ...(pick ? { pick } : {}) };
  })() : null;
  const nestedSource = input.useAbilityVariable1 === 'nested_inventory_source';
  const consumeInventoryId = nestedSource ? upper(input.useAbilityVariable2) : input.consumeInventoryId;
  const janusInput = {
    casterId: upper(input.casterId), targetIds: finalIds, abilityId: upper(input.abilityId),
    rawDiceMod: input.rawDiceMod ?? 0, rawDiceAdvDisStacks: input.rawDiceAdvDisStacks ?? 0,
    useAbilityVariable1: nestedSource ? null : input.useAbilityVariable1 ?? null, useAbilityVariable2: nestedSource ? null : input.useAbilityVariable2 ?? null,
    ...(consumeInventoryId ? { consumeInventoryId } : {}),
    ...(input.consumeInventoryUnit ? { consumeInventoryUnit: true as const } : {}),
    hurlInventoryId: input.hurlInventoryId,
    throwLob: input.throwLob,
    castingTargets: input.placed?.targetId ?? '',
    ...(paintedWall ? { paintedWall } : {}),
    // absent when the host did not hand the raw selection in (a timed start then hands back — it cannot store what PHP would)
    ...(input.placed ? { selection: input.placed.targetId } : typeof input.selection === 'string' ? { selection: input.selection } : {}),
  };
  // STATUS PROVENANCE (owner 2026-10-08, "browser-side check only"): every status this use would submit traces to a source the cast reaches —
  // its own (caster-choice-narrowed) applier lists, a nested dispatch it ran, or an engine status (status-provenance.ts). Judged after the
  // commit shape gate on every dry run (pre-draw) and on the real run; a gap names the unsupported browser decision.
  const provenance = (c: JanusCtx): void => {
    const gap = statusProvenanceGap({ abilityId: janusInput.abilityId, variable1: janusInput.useAbilityVariable1, childId: volley?.childId ?? null,
      statuses: c.statusesAdded, rows: c.rows, members: c.members, statusesRemoved: c.statusesRemoved }, read);
    if (gap) throw new HandBack(gap);
  };
  // Resolve every possible branch on throw-away clones before real dice. Missing
  // effect inputs must not reveal dice and invite a retry; preview writes never survive.
  const rangedDispatches = upper(read.value(volley?.child ?? definition, 'roll_type')) !== SPELL_RANGED_ATTACK ? []
    : volley ? volley.selections.map((id, dispatch) => ({ targetIds: [id], dispatch })) : [{ targetIds: finalIds, dispatch: undefined }];
  /** A projectile BOUNDARY = PHP's next separate janus() call, which re-reads (and so re-compiles) every row: the rows earlier
   *  projectiles added become STORED for the single-dispatch pending rules (ctx.priorDispatchStatuses — the Fortunate's next-roll ADV),
   *  and every member whose COMPILED view those changes move (a DynamicLayer family — Maimed's skill_rolls_mod …) is recompiled from
   *  its use-start row plus this use's status changes (member-status-recompile.ts). What cannot be recompiled here hands back. */
  const volleyBoundary = (c: JanusCtx): void => {
    c.priorDispatchStatuses = new Set(c.statusesAdded);
    const changed = new Set<string>();
    for (const s of c.statusesAdded) if (volleyRollAffecting(read, upper(s.status_item_id))) changed.add(upper(s.target));
    for (const r of c.statusesRemoved) if (volleyRollAffecting(read, upper(r.status_item_id))) changed.add(upper(r.target));
    for (const s of c.statusStacks ?? []) if (volleyRollAffecting(read, upper(s.status_item_id))) changed.add(upper(s.target));
    for (const id of changed) {
      const member = c.members.get(id), original = c.originals.get(id);
      if (!member || !original) throw new HandBack('volley:recompile-member-not-held');
      if (!read.recompileStatuses) throw new HandBack('volley:recompile-not-available');
      const steps: MemberStatusStep[] = [];
      for (const r of c.statusesRemoved.filter((x) => upper(x.target) === id)) {
        const stored = read.statuses(original).find((s) => upper(s.status_item_id ?? itemField(record(s.status_arr), 'item_id')) === upper(r.status_item_id));
        if (!stored?.status_id) throw new HandBack('volley:recompile-removed-row-not-held');
        steps.push({ kind: 'remove', statusItemId: upper(r.status_item_id), statusId: String(stored.status_id) });
      }
      // The status-chain compiler already supports remove + add with a rebound id. Rebuild a held quantity row at its final count;
      // its persisted identity stays fixed, and the commit still carries only the native stack delta.
      let replaced = 0;
      for (const s of (c.statusStacks ?? []).filter(s => upper(s.target) === id && s.before !== s.status_stack)) {
        if (c.statusesRemoved.some(r => upper(r.status_id) === upper(s.status_id))) continue;
        const stored = read.statuses(original).find(row => upper(row.status_id) === upper(s.status_id));
        if (!stored) throw new HandBack('volley:recompile-stack-row-not-held');
        steps.push({ kind: 'remove', statusItemId: upper(s.status_item_id), statusId: s.status_id },
          { kind: 'add', statusItemId: upper(s.status_item_id), casterId: upper(stored.status_caster_id) || id,
            stack: s.status_stack, statusId: s.status_id });
        replaced++;
      }
      for (const s of c.statusesAdded.filter((x) => upper(x.target) === id))
        steps.push({ kind: 'add', statusItemId: upper(s.status_item_id), casterId: upper(s.caster), stack: Number(s.status_stack ?? 1) || 1 });
      if (steps.length > 8) throw new HandBack('volley:recompile-too-many-steps');
      const result = read.recompileStatuses(original, steps, [...c.members.values()]); // the scene reads this use's current members
      // Dry-run catches add preflight; actual-outcome gaps must name their phase for the same exact-id hydrator.
      if (!result.ok) throw new HandBack(`${c.dryRun ? '' : 'commit:'}volley:recompile:${result.reason}`);
      // The recompile starts from the use-start vitality: a moved TOTAL would need this use's damage re-based — not modelled.
      const vitality = record(record(member.computed)?.vitality) ?? {};
      for (const key of ['v_max', 'effective_v_max', 'v_max_temp'])
        if (key in result.vitality && key in vitality && String(result.vitality[key]) !== String(vitality[key])) throw new HandBack('volley:recompile-vitality-moved');
      applyMemberStatusRoots(member, result);
      // Later dispatches read the projected clusters, but may spend an ability's charge.
      // Borrow immutable branches; give charge writers the existing run-local field view.
      const creature = { ...result.creature };
      if (record(creature.abilities)) creature.abilities = frameCopyOnRead(
        { abilities: creature.abilities }, new Map(), { catalogs: 'share' }).encounter.abilities;
      member.computed = { ...(record(member.computed) ?? {}), creature };
      if (result.catalog) member.catalog = result.catalog;
      // the derived clusters cite the compile's provisional row ids; the pending rows carry them until the commit re-keys every row
      const added = c.statusesAdded.filter((x) => upper(x.target) === id);
      added.forEach((s, k) => { (s as unknown as { status_id?: string }).status_id = result.addIds[replaced + k]; });
    }
  };
  const possibleTargetIds = (c: JanusCtx): Set<string> => {
    const candidates = new Set([...finalIds, ...(volley?.selections ?? []), ...(volleyAreas?.flatMap(area => area.targetIds) ?? [])]);
    const caster = c.members.get(upper(input.casterId))!;
    if (isSelfFollowAura(c, definition))
      for (const row of expandSelfFollowAuraTargets(c, definition, caster)) candidates.add(actorId(row));
    for (const { targetIds } of rangedDispatches)
      for (const id of targetIds) {
        const intended = c.members.get(id) ?? placedProps.get(id);
        if (intended) for (const row of strayCandidates(c, caster, intended)) candidates.add(actorId(row));
      }
    return candidates;
  };
  // Dying can land on mid rolls the extreme dry runs never reach. Check its gain
  // listeners on possible recipients, including auras, volleys and ranged strays.
  const recipients = possibleTargetIds(ctx);
  const gainGap = statusGainTriggerPossible(ctx, definition, caster, recipients)
    ?? (volley ? statusGainTriggerPossible(ctx, volley.child, caster, recipients) : null);
  if (gainGap) return { kind: 'handback', reason: `janus:unmodelled:status-gain-trigger:${gainGap}` };
  /** Hydrate only props this run can reach; distant floor rows keep their existing run-owned clones. */
  const runProps = new WeakMap<JanusCtx, Map<string, Row>>();
  const targetRows = (c: JanusCtx, ids = finalIds): Row[] => {
    const candidates = possibleTargetIds(c);
    let props = runProps.get(c);
    if (!props) {
      props = new Map([...placedProps].filter(([id]) => candidates.has(id))
        .map(([id, row]) => [id, inventoryDamageTarget(c.read, row, c.encounter)]));
      runProps.set(c, props);
    }
    // in place, by index (fix-bug 2026-10-14): an UNREAD floor row whose snapshot provably came from this use's source row is looked up by
    // the source's id — never parsed for the lookup (unreadSourceRow; resolveAbilityUse never mutates its input encounter)
    const floor = c.encounter.encounter_inventories_arr, sourceFloor = encounter.encounter_inventories_arr;
    if (!Array.isArray(floor)) c.encounter.encounter_inventories_arr = [];
    else if (props.size) for (let i = 0; i < floor.length; i++) if (i in floor) {
      const prop = props.get(inventoryId((unreadSourceRow(floor, i, sourceFloor) ?? floor[i]) as Row));
      if (prop) floor[i] = prop;
    }
    c.objectDamage ??= []; c.saveObjectDamage = true;
    c.wallRunAoeSections = wallRunAoeSections; c.wallRunCarved ??= new Map();
    c.wallRunAoeAction = wallRunActionKey(caster, input.abilityId);
    return ids.map(id => c.members.get(upper(id)) ?? props.get(upper(id)) as Row);
  };
  /** Prop removals use inventory_status_targets. Charges and creature columns still have no prop commit;
   * every dry run checks these writes before the real draw. */
  const propWriteGate = (c: JanusCtx, { checkOnly = false } = {}): void => {
    if (!placedProps.size) return;
    // A capped prop stack has neither a write nor a log row. Keep every actual change on the normal stack commit lane.
    if (!checkOnly && c.statusStacks) c.statusStacks = c.statusStacks.filter(s => s.before !== s.status_stack || !placedProps.has(upper(s.target)));
    if (c.chargesChanged.some(change => placedProps.has(upper(change.holder)))) throw new HandBack('janus:inventory-target:write:charge');
    // an UNREAD floor row (nothing this run wrote) whose snapshot provably came from the source row: its columns are the source's, read as
    // its JSON copy holds them (no parse)
    const floor = Array.isArray(c.encounter.encounter_inventories_arr) ? c.encounter.encounter_inventories_arr as unknown[] : [];
    let column: string | undefined;
    for (let i = 0; i < floor.length && column === undefined; i++) if (i in floor) {
      const source = unreadSourceRow(floor, i, encounter.encounter_inventories_arr);
      const columns = record(record(source ?? floor[i])?.encounter_creatures) ?? {};
      column = Object.keys(columns).filter(key => !source || JSON.stringify(columns[key]) !== undefined)[0];
    }
    if (column) throw new HandBack(`janus:inventory-target:write:column:${column}`);
  };
  /** The whole use on one ctx: the ability — then, for a volley, each projectile's child dispatch (use_ability_inc.php's loop: the
   *  target re-read from the live encounter, variables null, the child's own charge pool exempt — ctx.instantChild). */
  const perform = (c: JanusCtx, strayDispatch?: number): void => {
    const cCaster = c.members.get(upper(input.casterId)) as Row;
    c.read = { ...c.read, killTriggerRecorder: frame => () => {
      commitShapeGate(frame, { assignStatusIds: false }); propWriteGate(frame, { checkOnly: true }); provenance(frame); return null;
    } };
    if (control) { creatureControlRoll(c, janusInput, cCaster); return; }
    if (extraordinary) spendExtraordinary(c, cCaster, extraordinary);
    // Wizard v9 WP3: this caller persists summons (the commit's `summons` block) — every run starts its own list
    c.summons = [];
    c.spawnRows = { version: 'janus.spawn.rows.v1', writes: [], logs: [] };
    c.spawnTurnTransition = undefined;
    c.summonRequest = summonRequest;
    // Nested summons carry their own source identity; root descriptors can reuse this frame.
    c.summonOrigin = { abilityId: upper(input.abilityId), casterId: upper(input.casterId) };
    c.throwObjectFirstHit = undefined;
    const landing = isThrowObjectCast(definition, input) ? planThrowObjectLanding(c, janusInput, definition, cCaster,
      read.resolveInventory!(upper(input.useAbilityVariable2))!, finalIds) : null;
    const wentOffBefore = c.castsWentOff ?? 0;
    janus(c, janusInput, dispatchDefinition(definition), cCaster, [...targetRows(c),
      ...(summonRequest ? [summonPointTarget(input.encounterId, summonRequest.point)] : [])]);
    landing?.();
    drainAbilityDestructionPayloads(c);
    // a COUNTERSPELLED parent never succeeded → use_ability_inc.php breaks before the first projectile. A parent that did not GO OFF — a
    // failed armor bending roll or a hostile miscast: "the spell does nothing — no dispatch" (armor-bending.ts, hostile-spell-miscast.ts;
    // ctx.castsWentOff is exactly that gate) — fires none either: no projectile goes out, modified or not, and its Sanguine carriers stay
    // armed (settleNextSpell never ran). The action was spent by the template, the mana was not.
    if (!volley || c.counterspelled || (c.castsWentOff ?? 0) === wentOffBefore) return;
    const impacts = volleyAreas ?? volley.selections.map(selection => ({ targetIds: [selection], placement: null, sections: wallRunAoeSections }));
    // The creatures the projectiles REACHED (each projectile's effect gate queues them on its plan copy — queueNextSpellTarget), once each.
    const reached: string[] = [];
    for (const [dispatch, impact] of impacts.entries()) {
      volleyBoundary(c);
      const targets = impact.placement ? targetRows(c, impact.targetIds) : impact.targetIds.map(id => c.members.get(id)!);
      if (targets.some(target => !target)) throw new HandBack('volley:target-not-held');
      if (!impact.placement && targets.some(target => !['creature', 'player_character']
        .includes(String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '')))) throw new HandBack('janus:non-creature-target');
      c.wallRunAoeSections = impact.sections;
      c.wallRunAoeAllSections = volleyWallSections;
      c.wallRunAoeAction = wallRunActionKey(cCaster, volley.childId);
      c.instantChild = volley.childId;
      c.dispatchRowStart = c.rows.length;
      const probe = c.strayProbe;
      if (dispatch !== strayDispatch) c.strayProbe = null;
      const projectile = dispatchDefinition(volley.child);
      try {
        const selection = impact.placement?.targetId ?? impact.targetIds[0]!;
        janus(c, { ...janusInput, targetIds: impact.targetIds, abilityId: volley.childId, useAbilityVariable1: null, useAbilityVariable2: null,
          castingTargets: selection, ...(impact.placement ? { selection } : {}) }, projectile, cCaster, targets);
      } finally { c.instantChild = null; c.dispatchRowStart = 0; c.strayProbe = probe; }
      drainAbilityDestructionPayloads(c);
      for (const id of nextSpellPlan(projectile)?.targets ?? []) if (!reached.includes(id)) reached.push(id);
    }
    // The parent's Sanguine per-recipient payloads (Fearsome's rider, Umbral's zone, Encouraging's offer) on a volley: delivered ONCE, after
    // the last projectile, to each creature the volley reached — one rider per recipient however many projectiles hit it — from the carriers
    // the parent's settlement consumed once (settleNextSpell). Only a parent that went off reaches this loop (above); the settlement guard
    // keeps the delivery bound to the consumption all the same.
    const plan = nextSpellPlan(definition);
    if (plan?.volleyParent && plan.carriers.length && reached.length
      && plan.carriers.every(carrier => c.statusesRemoved.some(removed => upper(removed.status_id) === upper(carrier.status.status_id))))
      dispatchNextSpellPayloads(c, janusInput, { ...plan, targets: reached }, cCaster);
  };
  // RANDOM TABLES (lane A3 RT-4): the extreme dice reach only a table's end ranges, so every table a dry run rolled is re-run once per
  // outcome its ranges hand out (randomTableForced) — the WHOLE use, through the commit gate — before the real draw (Codex RT-4 review).
  const randomTableSeen = new Map<string, Array<string | null>>();
  const runs: Array<{ pattern: (typeof DRY_RUN_PATTERNS)[number]; forced: { tableId: string; attached: string | null } | null }> =
    DRY_RUN_PATTERNS.map(pattern => ({ pattern, forced: null }));
  const queuedOutcomes = new Set<string>();
  const done: DryRunTrace[] = [];
  const damageNeeds = new Map<string, InventoryDamageNeed>();
  let damageNeedReason = '';
  const collectDamageNeeds = (error: unknown, prefix: string): boolean => {
    if (!(error instanceof InventoryDamageFactsNeeded)) return false;
    damageNeedReason ||= prefix + error.reason;
    for (const need of error.needs) damageNeeds.set(`${need.kind}:${need.id}`, need);
    return true;
  };
  for (let i = 0; i < runs.length; i++) {
    const { pattern, forced } = runs[i]!;
    const runKey = forced ? `${forced.tableId}|${forced.attached ?? ''}` : '';
    if (dryRunTwin(done, runKey, pattern)) continue;
    const choices: boolean[] = [];
    const dry: JanusCtx = { ...ctx, encounter: copies.encounter(), members: copies.members(),
      roll: syntheticRollSource(pattern, choices), rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
      playerCharactersChanged: undefined, statusConnectedId: null, deathTail: null, concentrationBreaks: null, statusStacks: [], dryRun: true,
      randomTableSeen, randomTableForced: forced };
    try { withSurfaceReactionFrame(dry, () => perform(dry)); commitShapeGate(dry); propWriteGate(dry); provenance(dry); }
    catch (error) {
      if (collectDamageNeeds(error, 'preflight:')) continue;
      // KNOWN RESIDUALS (DRY_RUN_RESIDUALS; logged in the Lane A handoff as bias).
      if (error instanceof HandBack && DRY_RUN_RESIDUALS.has(error.reason)) continue;
      if (error instanceof HandBack) return unavailable(error, 'preflight:');
      if (error instanceof Refusal) continue;
      return { kind: 'handback', reason: `preflight:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
    } finally {
      done.push({ key: runKey, choices });
      for (const [tableId, outcomes] of randomTableSeen) for (const attached of outcomes) {
        const key = `${tableId}|${attached ?? ''}`;
        if (queuedOutcomes.has(key)) continue;
        queuedOutcomes.add(key);
        // both extremes per outcome: the rest of the use (its damage, its deaths) varies with the dice after the payload (Codex re-review)
        runs.push({ pattern: 'max', forced: { tableId, attached } }, { pattern: 'min', forced: { tableId, attached } });
      }
    }
    if (runs.length > DRY_RUN_PATTERNS.length + MAX_FORCED_TABLE_RUNS) return { kind: 'handback', reason: 'preflight:random_table_roll:too-many-outcomes-to-prove' };
  }
  // The extreme patterns reach at most one stray pick. Probe every candidate for each ranged dispatch
  // through the whole ordered use, including its parent cost and earlier children's effects, before the real draw.
  for (const { targetIds, dispatch } of rangedDispatches) {
    const probe: JanusCtx = { ...ctx, encounter: copies.encounter(), members: copies.members() };
    const probeTargets = targetRows(probe);
    const probeCaster = probe.members.get(upper(input.casterId)) as Row;
    const candidateIds = new Set<string>();
    try {
      const intendedTargets = volley ? targetIds.map(id => probe.members.get(id)) : probeTargets;
      for (const intended of intendedTargets) {
        if (intended) for (const candidate of strayCandidates(probe, probeCaster, intended)) candidateIds.add(actorId(candidate));
      }
    } catch (error) {
      if (error instanceof HandBack) return unavailable(error, 'preflight:');
      throw error;
    }
    // Each candidate is probed through the REAL cast (the intended targets and their own gates — PHP's retarget never re-runs
    // direct-target visibility / range on the stray token): every face is a 1 until the weighted pick, which is forced onto the
    // candidate, then the re-roll and damage run at max and at min. A refusal is the intended target's own (the main dry run
    // already judged it).
    for (const candidateId of candidateIds) {
      if (!members.has(candidateId) && !placedProps.has(candidateId)) return { kind: 'handback', reason: 'preflight:stray-candidate-not-held' };
      for (const post of ['max', 'min'] as const) {
        let picked = false;
        const roll: RollSource = {
          d(die: number): number { return picked && post === 'max' ? die : 1; },
          fraction(): number { return picked && post === 'max' ? 0.999999 : 0; },
        };
        const dry: JanusCtx = { ...ctx, encounter: copies.encounter(), members: copies.members(),
          roll, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
          playerCharactersChanged: undefined, statusConnectedId: null, deathTail: null, concentrationBreaks: null, statusStacks: [], dryRun: true,
          strayProbe: { candidateId, picked: () => { picked = true; } } };
        try { withSurfaceReactionFrame(dry, () => perform(dry, dispatch)); commitShapeGate(dry); propWriteGate(dry); provenance(dry); }
        catch (error) {
          if (collectDamageNeeds(error, 'preflight:stray:')) continue;
          if (error instanceof HandBack && DRY_RUN_RESIDUALS.has(error.reason)) continue;
          if (error instanceof HandBack) return unavailable(error, 'preflight:stray:');
          if (error instanceof Refusal) continue;
          return { kind: 'handback', reason: `preflight:stray:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
        }
      }
    }
  }
  if (damageNeeds.size) return { kind: 'handback', reason: damageNeedReason, needs: [...damageNeeds.values()] };
  if (consentPending(read)) return { kind: 'handback', reason: `preflight:${TRIGGER_CONSENT_REASON}` }; // OWNER RULING 2026-10-02: ask first
  withSurfaceReactionFrame(ctx, () => perform(ctx));
  if (consentPending(ctx.read)) throw new HandBack(`outcome:${TRIGGER_CONSENT_REASON}`);
  commitShapeGate(ctx);
  propWriteGate(ctx);
  provenance(ctx);
  if ('remaining' in roll && typeof roll.remaining === 'function' && roll.remaining() !== 0)
    return { kind: 'handback', reason: 'janus:roll-sequence-incomplete' };
  // A skill with no eligible roller completes without a log/save. A corpse-only
  // effect reports its ordinary target warning and discards any speculative costs.
  const deadTargetWarning = ctx.warnings?.find(warning => warning === DEAD_DAMAGE_WARNING || warning === DEAD_HEAL_WARNING);
  if (!ctx.rows.length && deadTargetWarning) return { kind: 'refuse', message: deadTargetWarning };
  if (!ctx.rows.length && !SKILL_ITEM_TYPES.has(String(itemField(definition, 'item_type') ?? '')))
    return { kind: 'handback', reason: 'janus:verdict-without-rows' };
  if (upper(input.abilityId) === HUNKER_ABILITY && input.hunkerObject) {
    for (const status of ctx.statusesAdded) if (status.status_item_id === HUNKER_STATUS && status.target === upper(input.casterId))
      bindHunkerStatus(status as unknown as Row, input.hunkerObject);
  }
  return resolvedVerdict(ctx);
  } catch (error) {
    if (error instanceof HandBack) return unavailable(error);
    if (error instanceof Refusal) return { kind: 'refuse', message: error.refusal };
    if (/^replay (?:draw|exhausted)/.test(String((error as Error)?.message ?? '')))
      throw error;
    return { kind: 'handback', reason: `exception:${String((error as Error)?.message ?? error)}` };
  }
}

/** A frame the GAME starts (not a player's cast): isolated field views per run, every hand-back decided on the extreme-dice dry runs before the
 *  real draw — resolveMoveTriggers' shape, shared by the engine-fired casts and the standalone skill roll below. */
export function resolveEngineFrame<T>(input: { rollPrivate?: number; dryRun?: boolean }, encounter: Row, members: ReadonlyMap<string, Row>, read: AbilityUseReaders, roll: RollSource,
  preview: (c: JanusCtx) => void, perform: (c: JanusCtx) => T, inert?: (c: JanusCtx) => boolean): { kind: 'resolved'; ctx: JanusCtx; value: T } | { kind: 'handback'; reason: string } {
  read = abilityFrameReaders(read);
  const randomTableSeen = new Map<string, Array<string | null>>();
  const fresh = (r: RollSource, dryRun: boolean, forced: { tableId: string; attached: string | null } | null = null): JanusCtx => {
    const copy = frameCopyOnRead(encounter, members, { catalogs: 'share' });
    return { encounter: copy.encounter, members: copy.members,
      originals: members, read: abilityFrameReaders(read), roll: r, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
      statusConnectedId: null, rollPrivate: input.rollPrivate ?? 0, deathTail: null, concentrationBreaks: null, statusStacks: [],
      dryRun: dryRun || input.dryRun === true,
      ...(dryRun ? { randomTableSeen, randomTableForced: forced } : {}) };
  };
  try {
    // A frame with nothing to do — the caller's `inert`, judged by the engine's own readers on a fresh copy — has no preview work and no dry
    // outcome to prove (every dry run is that same no-op), so that copy is the real run. Anything `inert` cannot settle (a throw, a pending
    // consent) takes the full path below. fix-bug 2026-10-13: the start ran the full ten-copy frame per combatant per plan pass.
    if (inert && !consentPending(read)) {
      const ctx = fresh(roll, false);
      let quiet = false;
      try { quiet = inert(ctx); } catch { quiet = false; }
      if (quiet) {
        const value = perform(ctx);
        commitShapeGate(ctx);
        if ('remaining' in roll && typeof roll.remaining === 'function' && roll.remaining() !== 0)
          return { kind: 'handback', reason: 'janus:roll-sequence-incomplete' };
        return { kind: 'resolved', ctx, value };
      }
    }
    try { preview(fresh(roll, true)); } catch (error) {
      if (error instanceof HandBack) return { kind: 'handback', reason: `preflight:${error.reason}` };
      return { kind: 'handback', reason: `preflight:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
    }
    // the extreme dice, then one whole-frame run per random-table outcome a run met (as resolveAbilityUse does)
    const runs: Array<{ pattern: (typeof DRY_RUN_PATTERNS)[number]; forced: { tableId: string; attached: string | null } | null }> =
      DRY_RUN_PATTERNS.map(pattern => ({ pattern, forced: null }));
    const queued = new Set<string>();
    const done: DryRunTrace[] = [];
    for (let i = 0; i < runs.length; i++) {
      const runKey = runs[i]!.forced ? `${runs[i]!.forced!.tableId}|${runs[i]!.forced!.attached ?? ''}` : '';
      if (dryRunTwin(done, runKey, runs[i]!.pattern)) continue;
      const choices: boolean[] = [];
      const dry = fresh(syntheticRollSource(runs[i]!.pattern, choices), true, runs[i]!.forced);
      try { perform(dry); commitShapeGate(dry); }
      catch (error) {
        if (error instanceof HandBack && DRY_RUN_RESIDUALS.has(error.reason)) continue;
        if (error instanceof HandBack) return { kind: 'handback', reason: `preflight:${error.reason}` };
        if (error instanceof Refusal) return { kind: 'handback', reason: 'preflight:engine-frame:refusal' };
        return { kind: 'handback', reason: `preflight:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
      } finally {
        done.push({ key: runKey, choices });
        for (const [tableId, outcomes] of randomTableSeen) for (const attached of outcomes) {
          const key = `${tableId}|${attached ?? ''}`;
          if (!queued.has(key)) { queued.add(key); runs.push({ pattern: 'max', forced: { tableId, attached } }, { pattern: 'min', forced: { tableId, attached } }); }
        }
      }
      if (runs.length > DRY_RUN_PATTERNS.length + MAX_FORCED_TABLE_RUNS) return { kind: 'handback', reason: 'preflight:random_table_roll:too-many-outcomes-to-prove' };
    }
    if (consentPending(read)) return { kind: 'handback', reason: `preflight:${TRIGGER_CONSENT_REASON}` }; // OWNER RULING 2026-10-02: ask first
    const ctx = fresh(roll, false);
    const value = perform(ctx);
    if (consentPending(ctx.read)) throw new HandBack(`outcome:${TRIGGER_CONSENT_REASON}`);
    commitShapeGate(ctx);
    if ('remaining' in roll && typeof roll.remaining === 'function' && roll.remaining() !== 0)
      return { kind: 'handback', reason: 'janus:roll-sequence-incomplete' };
    return { kind: 'resolved', ctx, value };
  } catch (error) {
    if (error instanceof HandBack) return { kind: 'handback', reason: error.reason };
    if (error instanceof Refusal) return { kind: 'handback', reason: 'engine-frame:refusal' };
    if (/^replay (?:draw|exhausted)/.test(String((error as Error)?.message ?? ''))) throw error;
    return { kind: 'handback', reason: `exception:${String((error as Error)?.message ?? error)}` };
  }
}
export type TimedFinishVerdict = (Extract<AbilityUseVerdict, { kind: 'resolved' }> & TimedFinishOutcome) | { kind: 'handback'; reason: string };
/** A2 supplies post-hit member copies. The ordinary ability executor owns every reaction rule and roll. */
export interface AttackDamageInput { actorId: string; targetId: string; damageTypes: string[]; damage: boolean;
  zeroVitality: boolean; targetJustKilled: boolean; dying?: { weaponId: string; damage: number; critical?: boolean };
  killed?: { weaponId: string; damage: number; critical?: boolean }; targetDying?: boolean; targetCriticalMaimed?: boolean; damageBefore?: number; damageTotal?: number; weaponId?: string;
  nextRollAgainstConsumed?: Array<{ targetId: string; statusId: string }>;
  onHit?: { weapon: Row; rollType: string; critical: boolean; skipTargetTail?: boolean };
  postRoll?: { rollType: string; hit: boolean }; preview?: boolean; dryRun?: boolean; rollPrivate?: number }

/** A compound attack owns one mutable frame across its paid hit and rider. */
export function attackDamageFrame(encounter: Row, members: ReadonlyMap<string, Row>, read: AbilityUseReaders,
  roll: RollSource, rollPrivate = 0, prepared?: FrameCopy): JanusCtx {
  return { ...(prepared ?? frameSnapshot(encounter, members)()), originals: members, read: abilityFrameReaders(read), roll,
    rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
    statusConnectedId: null, rollPrivate, statusStacks: [], deathTail: null, concentrationBreaks: null };
}

export function applyAttackDamageReactions(ctx: JanusCtx, input: AttackDamageInput): void {
  consumeAttackRollStatuses(ctx, input);
  const caster = ctx.members.get(upper(input.actorId)), target = ctx.members.get(upper(input.targetId));
  if (!caster || !target) throw new HandBack('attack:reaction-members-not-held');
  const zero = input.killed ?? input.dying;
  const weaponId = zero?.weaponId ?? input.weaponId ?? '';
  const use = { casterId: input.actorId, abilityId: weaponId, targetIds: [input.targetId],
    rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null };
  const status = input.damage && input.damageTotal !== undefined ? planDamageStatusEffects(ctx, caster, target) : null;
  if (input.damage) { const weapon = ctx.read.definition(weaponId) ?? input.onHit?.weapon;
    if (weapon) surfaceDamageImpact(ctx, weapon, caster, target, input.damageTypes);
  }
  if (input.onHit) {
    runK4OnHit(ctx, use, input.onHit.weapon, caster, target, input.onHit.critical, input.onHit.rollType,
      { skipTargetTail: input.onHit.skipTargetTail });
  }
  const maimedBefore = damageMaimedStacks(ctx, target);
  if (input.targetCriticalMaimed) {
    const weapon = ctx.read.definition(weaponId);
    if (!weapon) throw new HandBack('attack:maimed-weapon-not-held');
    applyDamageMaimed(ctx, use, weapon, caster, target);
  }
  if (zero) {
    const weapon = ctx.read.definition(zero.weaponId);
    if (!weapon) throw new HandBack('attack:dying-weapon-not-held');
    (target.encounter_creatures as Row).v_current = 0;
    patchVitalityCluster(target, 0);
    if (input.damage) fireDamageContingencies(ctx, target, input.damageBefore ?? 0);
    if (Number(ecLean(target, 'v_current')) <= 0) resolvePlayerDying(ctx, use, weapon, caster, target, zero.damage, zero.critical);
  } else {
    if (input.damage) fireDamageContingencies(ctx, target, input.damageBefore ?? Number(ecLean(target, 'v_current')));
    if (input.zeroVitality && Number(ecLean(target, 'v_current')) <= 0) onKillTriggers(ctx, caster, target);
  }
  const dead = ctx.composedDeaths?.includes(upper(input.targetId)) === true;
  const killedCreature = dead && String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '') !== 'player_character';
  if (status?.wake && input.damage) performWake(ctx, status.wake, caster, target, input.zeroVitality);
  if (status && input.damage) performDamageStatusEffects(ctx, use, caster, target, status, input.damageBefore ?? Number(ecLean(target, 'v_current')));
  if (status?.retry && input.damage && !input.zeroVitality) performContestRetry(ctx, use, status.retry, target);
  if (input.damage) damageTakenTriggers(ctx, { casterId: input.actorId } as Parameters<typeof damageTakenTriggers>[1],
    caster, target, input.damageTypes, killedCreature, input.onHit?.critical ?? false, true);
  if (status?.concentrating && Number(input.damageTotal) > 0 && !dead)
    finishDamageConcentration(ctx, use, target, status.concentrating, input.damageTotal!, false,
      { maimedBefore: maimedBefore - Number(!!input.targetDying && !zero) });
  if (input.postRoll) runPostRollRetaliation(ctx, caster, target, input.postRoll.rollType, input.postRoll.hit);

}

function consumeAttackRollStatuses(ctx: JanusCtx, input: AttackDamageInput): void {
    for (const consumed of input.nextRollAgainstConsumed ?? []) {
      // A prior single-connection removal may already have retired this row as its peer.
      if (ctx.statusesRemoved.some(s => upper(s.status_id) === upper(consumed.statusId))) continue;
      const member = ctx.members.get(upper(consumed.targetId));
      const status = member && ctx.read.statuses(member).find(s => upper(statusScalar(s, 'status_id')) === upper(consumed.statusId));
      if (!member || !status) throw new HandBack('remove_status_effects:stored-row-not-held');
      removeStoredStatus(ctx, member, status, 'single');
    }
}

export function resolveAttackDamageReactions(input: AttackDamageInput, encounter: Row,
  members: ReadonlyMap<string, Row>, read: AbilityUseReaders, roll: RollSource, frame?: JanusCtx): AbilityUseVerdict {
  const participants = (ctx: JanusCtx): [Row, Row] => {
    const caster = ctx.members.get(upper(input.actorId)), target = ctx.members.get(upper(input.targetId));
    if (!caster || !target) throw new HandBack('attack:reaction-members-not-held');
    return [caster, target];
  };
  const preview = (ctx: JanusCtx): void => {
    consumeAttackRollStatuses(ctx, input);
    const [caster, target] = participants(ctx);
    if (input.damage && input.damageTotal !== undefined) planDamageStatusEffects(ctx, caster, target);
    if (input.postRoll) postRollReactionsPreDraw(ctx, caster, target, input.postRoll.rollType);
    if (input.zeroVitality) onKillGate(ctx, caster, target);
    if (!input.damage) return;
    const pair = (owner: Row, inciting: Row, any: string, typed: string): void => {
      const original = owner.encounter_creatures;
      try {
        if (any === TAKES_ANY_DAMAGE)
          triggerFamilyPreDraw(ctx, owner, { trigger: any, damageTypes: input.damageTypes, damagePhase: 'before' }, undefined, inciting);
        if (triggerFamilyPreDraw(ctx, owner, { trigger: any, damageTypes: input.damageTypes,
          ...(any === TAKES_ANY_DAMAGE ? { damagePhase: 'after' as const } : {}) }, undefined, inciting))
          owner.encounter_creatures = { ...record(original), reaction: 0 };
        triggerFamilyPreDraw(ctx, owner, { trigger: typed, damageTypes: input.damageTypes }, undefined, inciting);
      } finally { owner.encounter_creatures = original; }
    };
    pair(target, caster, TAKES_ANY_DAMAGE, TAKES_DAMAGE_TYPE);
    pair(caster, target, TARGET_TAKES_ANY_DAMAGE, TARGET_TAKES_DAMAGE_TYPE);
  };

  const perform = (ctx: JanusCtx): void => {
    if (input.preview) { preview(ctx); return; }
    applyAttackDamageReactions(ctx, input);
  };
  // A primary swing and its defender costs/retaliation may already own this exact mutable frame.
  if (frame) {
    try {
      frame.read = abilityFrameReaders(frame.read);
      perform(frame); commitShapeGate(frame); return resolvedVerdict(frame);
    } catch (error) {
      if (error instanceof HandBack) return { kind: 'handback', reason: error.reason };
      if (error instanceof Refusal) return { kind: 'handback', reason: 'engine-frame:refusal' };
      if (/^replay (?:draw|exhausted)/.test(String((error as Error)?.message ?? ''))) throw error;
      return { kind: 'handback', reason: `exception:${String((error as Error)?.message ?? error)}` };
    }
  }
  const out = resolveEngineFrame(input, encounter, members, read, roll, preview, perform, input.preview ? ctx => { preview(ctx); return true; } : ctx => {
    if (input.onHit || input.postRoll || input.nextRollAgainstConsumed?.length) return false;
    if (input.zeroVitality || input.targetCriticalMaimed) return false;
    if (!input.damage) return true;
    const [caster, target] = participants(ctx), triggers = janusCtxTriggerReaders(ctx);
    if (input.damageTotal !== undefined) {
      const status = planDamageStatusEffects(ctx, caster, target);
      if (status.wake || status.retry || status.concentrating || status.charm || status.reveal.length || status.contingencies.length) return false;
    }
    for (const [owner, inciting, keys] of [[target, caster, [TAKES_ANY_DAMAGE, TAKES_DAMAGE_TYPE]],
      [caster, target, [TARGET_TAKES_ANY_DAMAGE, TARGET_TAKES_DAMAGE_TYPE]]] as const) {
      if (!ctx.read.reactionInventoryComplete(owner)) return false;
      for (const trigger of keys) {
        if (ctx.read.has(owner, trigger)) return false;
        const event = { trigger, damageTypes: input.damageTypes };
        const { carriers, statuses } = collectTriggerCandidates(triggers, owner, event);
        if ([...carriers, ...statuses].some(candidate => gateTriggerCandidate(triggers, candidate, owner, event, inciting).fire)) return false;
      }
    }
    return true;
  });
  if (out.kind === 'handback') return out;
  const verdict = resolvedVerdict(out.ctx);
  if (input.killed || input.dying) {
    const target = out.ctx.members.get(upper(input.targetId))!;
    // The paid swing proposes zero first; a Last Stand can restore the original value.
    // Carry that explicit final value even when a diff against the original is empty.
    verdict.ec[input.targetId] = { ...verdict.ec[input.targetId],
      v_current: Number(ecLean(target, 'v_current') ?? 0), encounter_creature_dead: Number(ecLean(target, 'encounter_creature_dead') ?? 0) };
  }
  return verdict;
}
/** The weapon transport uses the same status application/removal executor and preflight as an ability. */
export function resolveAttackStatusEffects(input: { actorId: string; targetId: string; shieldedHits: number; fortunate: boolean; rollPrivate?: number },
  encounter: Row, members: ReadonlyMap<string, Row>, read: AbilityUseReaders, roll: RollSource): AbilityUseVerdict {
  const out = resolveEngineFrame(input, encounter, members, read, roll, () => {}, ctx => {
    const caster = ctx.members.get(upper(input.actorId)), target = ctx.members.get(upper(input.targetId));
    if (!caster || !target) throw new HandBack('attack:status-members-not-held');
    if (input.fortunate) applyUniversalFortunateOnLuckGain(ctx, caster);
    for (let i = 0; i < input.shieldedHits; i++) consumeShielded(ctx, caster, target);
  });
  return out.kind === 'handback' ? out : resolvedVerdict(out.ctx);
}
/** One mandatory page-observed completion, with the same extreme-dice / consent preflight as every engine frame. */
export function resolveTimedFinish(input: TimedFinishInput, encounter: Row, members: ReadonlyMap<string, Row>, read: AbilityUseReaders,
  roll: RollSource): TimedFinishVerdict {
  const out = resolveEngineFrame(input, encounter, members, read, roll, c => {
    const status = [...c.members.values()].flatMap(m => read.statuses(m)).find(s => upper(s.status_id) === upper(input.statusId));
    // Natural 1/20 have their own dust outcomes. Prove the lowest ordinary face as well, before any live die.
    if (status && timedActivityFamily(status) === 'disenchanting') {
      performTimedFinish(c, input, read, true);
    }
  }, c => performTimedFinish(c, input, read));
  return out.kind === 'handback' ? out : { ...resolvedVerdict(out.ctx), ...out.value,
    ...(out.ctx.summons?.length ? { summons: out.ctx.summons } : {}) };
}

export const AFTER_COMBAT_BEGINS = 'triggers_after_combat_begins';
/** R-128 (Janus lane A3, 2026-10-01): the ENGINE-FIRED casts the combat boundaries make, decided in the browser for janus-engine/combat/**
 *  (lane N5) to post with the start / exit they ride:
 *   · `contingencies` — ft_fire_contingency_triggers() → ft_contingency_fire_cast(): a spell ARMED earlier (its mana pre-paid by the
 *     arming) fires ON ITS OWNER (caster = target) in trigger mode — no cast gate, no AP debit, no mana debit — after a
 *     `contingency_triggered` row; cast at the level it was armed at when that is a real upcast. A spell that grew past the armed level
 *     (PHP tops the mana up or refunds) or is no longer held hands back.
 *   · `afterCombatBegins` — janus_trigger('triggers_after_combat_begins') on each combatant: its self-only carriers fire on itself.
 *  The claim of a contingency row (its atomic one-fire) stays the committing endpoint's job; this decides what the fires write. */
export interface EngineFiredInput {
  encounterId: string;
  rollPrivate?: number;
  contingencies?: ContingencyCast[];
  afterCombatBegins?: string[];
}
export function resolveEngineFiredCasts(input: EngineFiredInput, encounter: Row, members: ReadonlyMap<string, Row>, read: AbilityUseReaders,
  roll: RollSource): AbilityUseVerdict {
  const contingencies = input.contingencies ?? [], begins = (input.afterCombatBegins ?? []).map(upper);
  if (!contingencies.length && !begins.length) return { kind: 'handback', reason: 'engine-fired:empty' };
  const owner = (c: JanusCtx, id: string): Row => { const row = c.members.get(upper(id)); if (!row) throw new HandBack('engine-fired:owner-not-held'); return row; };
  const event = { trigger: AFTER_COMBAT_BEGINS } as const;
  // Inert: no contingency to fire, and for every begins owner none of what triggerFamilyPreDraw / runTriggerFamily would act on — the trigger
  // layer on the row itself, an incomplete reaction inventory, or a candidate from the SAME collector the dispatch uses.
  const inert = contingencies.length ? undefined : (c: JanusCtx): boolean => begins.every((id) => {
    const row = owner(c, id);
    if (c.read.has(row, event.trigger) || !c.read.reactionInventoryComplete(row)) return false;
    const { carriers, statuses } = collectTriggerCandidates(janusCtxTriggerReaders(c), row, event);
    return carriers.length === 0 && statuses.length === 0;
  });
  const out = resolveEngineFrame(input, encounter, members, read, roll,
    (c) => { for (const id of begins) triggerFamilyPreDraw(c, owner(c, id), event); },
    (c) => {
      for (const fire of contingencies) fireContingencyCast(c, fire);
      for (const id of begins) { const row = owner(c, id); c.statusConnectedId = null; runTriggerFamily(c, event, row, null, [row], false, triggerExecutorGap, true); }
    }, inert);
  return out.kind === 'handback' ? out : resolvedVerdict(out.ctx);
}

/** R-129 (lane A3, for the TIME lane): the status GAIN / LOSS trigger events a status change decided OUTSIDE a cast fires — the hunger tick's
 *  hunger status gained, a transient flag lost (janus-engine/turns/time-push.ts withheld those PCs). Per event, in order, the twin of
 *  apply_status_effect's gain arms (the gainer's triggers_when_caster_gains_status_effect, inciting = the applier; then — unless self-applied —
 *  the applier's triggers_when_target_gains_status_effect, inciting = the gainer) or status_removal_api's loss arm (the loser's
 *  triggers_when_caster_loses_status_effect, no inciting row). Every dispatch runs through janus() in trigger mode; every hand-back is pre-draw.
 *  The caller writes the status change itself and posts this verdict's rows / changes with it (its rows follow the change). */
export interface StatusTriggerEvent { creatureId: string; statusItemId: string; kind: 'gain' | 'loss'; applierId?: string | null }
export function resolveStatusTriggerEvents(input: { encounterId: string; rollPrivate?: number; events: StatusTriggerEvent[] }, encounter: Row,
  members: ReadonlyMap<string, Row>, read: AbilityUseReaders, roll: RollSource): AbilityUseVerdict {
  if (!input.events.length) return { kind: 'handback', reason: 'status-trigger:empty' };
  if (input.events.length > 32) return { kind: 'handback', reason: 'status-trigger:too-many-events' };
  const member = (c: JanusCtx, id: string): Row => { const row = c.members.get(upper(id)); if (!row) throw new HandBack('status-trigger:member-not-held'); return row; };
  const GAIN = 'triggers_when_caster_gains_status_effect', TARGET_GAIN = 'triggers_when_target_gains_status_effect', LOSS = 'triggers_when_caster_loses_status_effect';
  const plan = (c: JanusCtx, run: boolean): void => {
    for (const e of input.events) {
      const owner = member(c, e.creatureId), statusItemId = upper(e.statusItemId);
      if (e.kind === 'loss') {
        if (run) runTriggerFamily(c, { trigger: LOSS, statusItemId }, owner, null, [owner], false, triggerExecutorGap, true);
        else triggerFamilyPreDraw(c, owner, { trigger: LOSS, statusItemId });
        continue;
      }
      const applier = e.applierId ? member(c, e.applierId) : owner;
      const self = upper(e.applierId ?? e.creatureId) === upper(e.creatureId);
      if (run) {
        c.statusConnectedId = null;
        runTriggerFamily(c, { trigger: GAIN, statusItemId }, owner, applier, [owner], false);
        if (!self) runTriggerFamily(c, { trigger: TARGET_GAIN, statusItemId }, applier, owner, [applier], false);
      } else {
        triggerFamilyPreDraw(c, owner, { trigger: GAIN, statusItemId }, triggerExecutorGap, applier);
        if (!self) triggerFamilyPreDraw(c, applier, { trigger: TARGET_GAIN, statusItemId }, triggerExecutorGap, owner);
      }
    }
  };
  const out = resolveEngineFrame(input, encounter, members, read, roll, (c) => plan(c, false), (c) => plan(c, true));
  return out.kind === 'handback' ? out : resolvedVerdict(out.ctx);
}

/** A SKILL ROLL with no use around it (skill_roll_arr() for a creature) — for the combat start's initiative (lane N5): the browser rolls
 *  the creature's best of `skillIds` (select_skill_for_contesting_roll) and returns the total and the rows it logs. Pre-draw fair like every
 *  frame here; no JanusCtx needed by the caller. */
export function resolveStandaloneSkillRoll(input: { encounterId: string; creatureId: string; skillIds: string[]; rollItemId?: string | null; rollPrivate?: number },
  encounter: Row, members: ReadonlyMap<string, Row>, read: AbilityUseReaders, roll: RollSource):
  ({ total: number } & Extract<AbilityUseVerdict, { kind: 'resolved' }>) | { kind: 'handback'; reason: string } {
  const skillIds = input.skillIds.map(upper).filter(Boolean);
  if (!skillIds.length) return { kind: 'handback', reason: 'skill-roll:no-skill' };
  const perform = (c: JanusCtx): number => {
    const creature = c.members.get(upper(input.creatureId));
    if (!creature) throw new HandBack('skill-roll:creature-not-held');
    const skill = selectSkillForContestingRoll(c, creature, skillIds);
    // The LOGGED roll_type_1 is skill_roll_arr's `layer_value(item_id_used_arr, 'roll_type') ?? item_type`: roll_initiative_for_caster's
    // used-item array carries roll_type at its ROOT (layer_value reads none) and item_type 'innate_skill' → the row says 'innate_skill'
    // (389 real rows), and the effective roll types are the generic skill_roll alias alone — no CMRT fire (lane A4 for N5b, 2026-10-01).
    const used = read.definition(upper(input.rollItemId ?? skillIds[0]));
    const usedRollType = used ? upper(read.value(used, 'roll_type')) : '';
    const rowType = usedRollType || String(itemField(used, 'item_type') ?? '') || SKILL_ROLL_TYPE;
    return skillRollArr(c, { casterId: upper(input.creatureId), targetIds: [], abilityId: upper(input.rollItemId ?? skillIds[0]), rawDiceMod: 0, rawDiceAdvDisStacks: 0,
      useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' }, creature, skill, null, 0,
      // initiative is an ordinary SKILL ROLL (PHP passes the Skill Roll type), not the contesting-roll default (Codex re-review)
      { isTarget: false, rollItemId: input.rollItemId ?? null, rollType: SKILL_ROLL_TYPE, rowType });
  };
  const out = resolveEngineFrame(input, encounter, members, read, roll, () => {}, perform);
  // the FULL change set (an ascension point, a consumed next-roll status, a carrier's charge) — the caller persists all of it (Codex re-review)
  if (out.kind === 'handback') return out;
  return { ...resolvedVerdict(out.ctx), total: out.value };
}

export { itemField };
